import { inject, Injectable } from '@angular/core';
import { MatDialog, MatDialogRef } from '@angular/material/dialog';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import { concatLatestFrom } from '@ngrx/operators';
import { Store } from '@ngrx/store';
import { FullscreenLayoutAreas, FullscreenLayoutTemplate } from '@src/assets/css/fullscreen.template.scss';
import { ModalRefResult, ModalResponseCodes, ModalService } from '@src/suite/common/components/modal/modal.service';
import { PSTFieldNames } from '@src/suite/common/data-models/fields/pst-field-names';
import { PublicationViewStateRecord } from '@src/suite/common/data-models/records/publication-viewstate-record';
import { SearchViewStateRecord } from '@src/suite/common/data-models/records/search-viewstate-record';
import { SingleViewStateRecord } from '@src/suite/common/data-models/records/single-viewstate-record';
import { ViewStateRecord } from '@src/suite/common/data-models/records/viewstate-record';
import { TreeLikeFlatNode } from '@src/suite/common/data-models/search-results/search-results';
import { SYSTEM_VS_GUID, SYSTEM_VS_NAME_RUNDOWN, SYSTEM_VS_NAME_TOPIC } from '@src/suite/common/enums/constants';
import { SearchType } from '@src/suite/common/enums/enums';
import { NavigationDirection } from '@src/suite/common/enums/navigation-direction';
import { ObjectTypes } from '@src/suite/common/enums/object-types';
import { ViewStateTypesEnum } from '@src/suite/common/enums/viewstate-types';
import { BasicContentService } from '@src/suite/common/services/basic-content-service';
import { GlobalTablesService } from '@src/suite/common/services/global-tables-service';
import { LayoutState } from '@src/suite/common/services/layout-state';
import { NomWebServicesJson } from '@src/suite/common/services/nom-web-services-json';
import { StoryService } from '@src/suite/common/services/story-service';
import { StFullScreenDialogComponent } from '@src/suite/common/st-full-screen-dialog/st-full-screen-dialog';
import {
    FourthSidecarActions,
    FullscreenActions,
    MainSidecarActions,
    OpenFullscreenModalActions,
    SecondSidecarActions,
    SidecarEfffectActions,
    ThirdSidecarActions,
} from '@src/suite/common/store/fullscreen/actions/fullscreen.actions';
import { CloseModalAction, SidecarMenu } from '@src/suite/common/store/fullscreen/models/fullscreen.models';
import {
    selectAllStoriesMap,
    selectAnyUnsavedChanges,
    selectCurrentRecord,
    selectCurrentRecordType,
    selectCurrentStoryIndex,
    selectCurrentStoryParentTopic,
    selectFullscreenCurrentViewRecord,
    selectFullscreenNewRecordDialogState,
    selectLayoutTemplate,
    selectSidecarAssignmentList,
    selectSidecarCurrentRundownSelectedStory,
    selectSidecarCurrentStory,
    selectSidecarCurrentTopicSelectedAssignment,
    selectSidecarCurrentTopicSelectedStory,
    selectSidecarCurrentTopicStoryGuids,
    selectSidecarFourthCurrentAssignmentViewState,
    selectSidecarFourthCurrentRundownViewState,
    selectSidecarFourthCurrentStoryViewState,
    selectSidecarFourthCurrentTopicViewState,
    selectSidecarMainCurrentAssignmentViewState,
    selectSidecarMainCurrentRundownViewState,
    selectSidecarMainCurrentStoryViewState,
    selectSidecarMainCurrentTopicViewState,
    selectSidecarSecondCurrentAssignmentViewState,
    selectSidecarSecondCurrentRundownViewState,
    selectSidecarSecondCurrentStoryViewState,
    selectSidecarSecondCurrentTopicViewState,
    selectSidecarSelectedAssignmentIndex,
    selectSidecarSelectedRundown,
    selectSidecarSelectedStoryIndex,
    selectSidecarSelectedTopic,
    selectSidecarStories,
    selectSidecarThirdCurrentAssignmentViewState,
    selectSidecarThirdCurrentRundownViewState,
    selectSidecarThirdCurrentStoryViewState,
    selectSidecarThirdCurrentTopicViewState,
    selectStoryRecordToEdit,
} from '@src/suite/common/store/fullscreen/selectors/fullscreen.selectors';
import { selectFieldDefsDict } from '@src/suite/common/store/global-tables/global-tables.selectors';
import { selectI18N } from '@src/suite/common/store/i18n/selectors/i18n.selectors';
import { ListActions } from '@src/suite/common/store/list/list.actions';
import { selectCurrentNodeUuid } from '@src/suite/common/store/list/list.selectors';
import { selectCurrentGroupBy, selectCurrentViewState } from '@src/suite/common/store/navigation/selectors/viewstate.selectors';
import { selectTreeControl } from '@src/suite/common/store/playbook/playbook.selector';
import { AssignmentRecordActions, RecordHttpActions, RundownRecordActions, StoriesRecordActions, TopicRecordActions } from '@src/suite/common/store/records/records.actions';
import {
    selectAssignmentRecordByGuid,
    selectRundownRecordByGuid,
    selectRundownRecordsMap,
    selectStoryTemplatesMap,
    selectTopicRecordByGuid,
    selectTopicRecordsMap,
    selectTopicTemplatesMap,
} from '@src/suite/common/store/records/records.selectors';
import { StoryHttpActions } from '@src/suite/common/store/story/actions/story.actions';
import { createTopicInGroup, initializeRecord } from '@src/suite/common/utils/record-utils';
import { extractLowestNodeLevel, navigateDirectly, navigateTo, navigateToStory } from '@src/suite/common/utils/store-utils';
import { getDatabasesOptions } from '@src/suite/common/utils/viewstate-utils';
import { DiscoverService } from '@src/suite/discover/services/discover-service';
import { LiveSearchService } from '@src/suite/live/services/live-search.service';
import { RundownService } from '@src/suite/live/services/rundown.service';
import { PlaybookSearchService } from '@src/suite/playbook/services/playbook-search-service';
import { PlaybookService } from '@src/suite/playbook/services/playbook-service';
import { TopicService } from '@src/suite/playbook/services/topic-service';
import { catchError, debounceTime, exhaustMap, filter, forkJoin, from, map, merge, of, switchMap, take, tap } from 'rxjs';
import { v4 } from 'uuid';

@Injectable()
export class FullscreenModalEffects {
    nomWebServicesJson = inject(NomWebServicesJson);
    topicService = inject(TopicService);
    storyService = inject(StoryService);
    dialog = inject(MatDialog);
    actions$ = inject(Actions);
    store = inject(Store);
    playbookService = inject(PlaybookService);
    playbookSearchService = inject(PlaybookSearchService);
    liveSearchService = inject(LiveSearchService);
    rundownService = inject(RundownService);
    basicContentService = inject(BasicContentService);
    globalTablesService = inject(GlobalTablesService);
    viewStateService = inject(LayoutState);
    modalService = inject(ModalService);
    discoverService = inject(DiscoverService);

    dialogRef: MatDialogRef<StFullScreenDialogComponent, any> = null;

    closeRundownFullscreenModal$ = createEffect(() => {
        return this.actions$.pipe(
            ofType(OpenFullscreenModalActions.closeRundwonFullscreenModal),
            concatLatestFrom(() => [this.store.select(selectCurrentRecordType)]),
            map(([_, currentRecordType]) => {
                if (currentRecordType === ObjectTypes.RUNDOWN) {
                    return OpenFullscreenModalActions.closeFullscreenModal();
                }
                return FullscreenActions.noOpAction();
            })
        );
    });

    closeFullscreenModal$ = createEffect(() => {
        return this.actions$.pipe(
            ofType(OpenFullscreenModalActions.closeFullscreenModal, OpenFullscreenModalActions.closeFullscreenModalForce),
            concatLatestFrom(() => [this.store.select(selectCurrentRecord), this.store.select(selectI18N), this.store.select(selectAnyUnsavedChanges)]),
            exhaustMap(([action, currentRecord, languageSettings, hasUnsavedChanges]) => {
                const okActions = [];
                okActions.push(FullscreenActions.resetState());
                if (!currentRecord?.isNewRecord && hasUnsavedChanges && currentRecord?.Type === ObjectTypes.SCRIPT) {
                    const storyRecord = currentRecord as StorytellingStoryRecord;
                    okActions.push(
                        ...[
                            StoryHttpActions.releaseStoryLock({ storyGuid: storyRecord?.Guid }),
                            RecordHttpActions.saveRecordChanges({
                                record: storyRecord,
                                fieldsToUpdate: [
                                    { key: PSTFieldNames.Type, value: ObjectTypes.SCRIPT },
                                    { key: PSTFieldNames.PSTRichText, value: storyRecord?.text },
                                    ...(storyRecord?.TextCommands ? [{ key: PSTFieldNames.TextCommands, value: storyRecord?.TextCommands }] : []),
                                ],
                            }),
                        ]
                    );
                }
                if (action.type === OpenFullscreenModalActions.closeFullscreenModal.type && (currentRecord?.isNewRecord || hasUnsavedChanges)) {
                    let modalRef: ModalRefResult = null;
                    switch (currentRecord?.Type) {
                        case ObjectTypes.TOPIC:
                            modalRef = this.modalService.openModal(
                                languageSettings.PSTWebConfirmCancelShort,
                                languageSettings.PSTWebConfirmCancel,
                                languageSettings.PSTWebYesOption,
                                languageSettings.PSTWebNoOption
                            );
                            break;
                        case ObjectTypes.SCRIPT:
                            modalRef = this.modalService.openConfirmation({
                                title: languageSettings.PSTWebConfirmSaveShort,
                                message: languageSettings.PSTWebConfirmSave,
                                yesLabel: languageSettings.PSTWebSaveOption,
                                noLabel: languageSettings.PSTWebDiscardOption,
                                cancelLabel: languageSettings.PSTWebCancelButtonLabel,
                            });
                            break;
                    }

                    if (!modalRef) {
                        return of(FullscreenActions.noOpAction());
                    }
                    return modalRef.result.pipe(
                        take(1),
                        exhaustMap(modalRes => {
                            if (modalRes === ModalResponseCodes.OK) {
                                this.dialogRef.close(CloseModalAction.CLOSE_MODAL);
                                return okActions;
                            } else {
                                switch (currentRecord?.Type) {
                                    case ObjectTypes.TOPIC:
                                        return of(FullscreenActions.noOpAction());
                                    case ObjectTypes.SCRIPT:
                                        if (modalRes === ModalResponseCodes.DISCARD) {
                                            this.dialogRef?.close(CloseModalAction.CLOSE_MODAL);
                                            return [StoriesRecordActions.setStoryHasUnsavedChanges({ storyGuid: currentRecord?.Guid, hasUnsavedChanges: false })];
                                        } else {
                                            return [FullscreenActions.noOpAction()];
                                        }
                                }
                            }
                        }),
                        catchError(() => {
                            return [FullscreenActions.noOpAction()];
                        })
                    );
                }
                this.dialogRef?.close(CloseModalAction.CLOSE_MODAL);
                return okActions;
            })
        );
    });

    openTopicFullscreenModal$ = createEffect(() => {
        return this.actions$.pipe(
            ofType(OpenFullscreenModalActions.openTopicFullscreenDialog, FullscreenActions.openTopic),
            concatLatestFrom(action => [this.store.select(selectTopicRecordByGuid(action.recordGuid)), this.store.select(selectCurrentRecord)]),
            switchMap(([action, storeRecord, currentRecord]) => {
                const record = action.topicRecord || storeRecord;
                const loadTopicData = async (topic: StorytellingTopicRecord, isMetadata, currentNewRecord: StorytellingTopicRecord) => {
                    if (currentNewRecord?.isNewRecord) {
                        return { ...currentNewRecord };
                    }

                    if (isMetadata) {
                        // TODO Handle record RTU and Store registration
                        const recordRes = await this.topicService.getTopicContent(this.nomWebServicesJson.getDatabase(), topic.Path.fullPath, topic.Guid);
                        topic = recordRes.data;
                    }
                    if (record?.Type === ObjectTypes.TOPIC) {
                        // TODO Handle record RTU and Store registration
                        const storiesRes = await this.topicService.getTopicStories(topic.Database, topic.Path.fullPath.substring(0, topic.Path.fullPath.lastIndexOf('\\')), topic.Guid);
                        topic.stories = storiesRes.data;
                    }
                    return topic;
                };

                return from(loadTopicData(record, action.isMetadata, currentRecord)).pipe(
                    exhaustMap(rec => {
                        const actions = [];
                        if (rec.isNewRecord) {
                            return of(FullscreenActions.updateCurrentNewRecord({ record: rec }));
                        }
                        if (action.isMetadata) {
                            actions.push(TopicRecordActions.updateTopics({ records: [rec] }));
                        }
                        if (action.type === OpenFullscreenModalActions.openTopicFullscreenDialog.type) {
                            actions.push(OpenFullscreenModalActions.openFullscreenModal());
                        }
                        if (rec.stories?.length > 0) {
                            actions.push(StoriesRecordActions.updateStories({ records: rec.stories }));
                        }
                        if (action.currentNodeUUID) {
                            actions.push(ListActions.navigateObject({ nodeUuid: action.currentNodeUUID }));
                        }
                        if (actions.length > 0) {
                            return actions;
                        }
                        return of(FullscreenActions.noOpAction());
                    })
                );
            })
        );
    });

    openRundownFullscreenModal$ = createEffect(() => {
        return this.actions$.pipe(
            ofType(OpenFullscreenModalActions.openRundownFullscreenDialog, FullscreenActions.openRundown),
            concatLatestFrom(action => [this.store.select(selectRundownRecordByGuid(action.recordGuid))]),
            switchMap(([action, storeRecord]) => {
                return from(this.rundownService.getRundown(storeRecord.Database, storeRecord.Path.fullPath, storeRecord.Guid)).pipe(
                    switchMap(res => {
                        const actions = [];
                        actions.push(RundownRecordActions.updateRundowns({ records: [res.data] }));
                        actions.push(StoriesRecordActions.updateStories({ records: res.data?.children }));
                        if (action.currentNodeUUID) {
                            actions.push(ListActions.navigateObject({ nodeUuid: action.currentNodeUUID }));
                        }
                        if (action.type === OpenFullscreenModalActions.openRundownFullscreenDialog.type) {
                            actions.push(OpenFullscreenModalActions.openFullscreenModal());
                        }
                        return actions;
                    })
                );
            })
        );
    });

    openFullscreenModal$ = createEffect(() => {
        return this.actions$.pipe(
            ofType(OpenFullscreenModalActions.openFullscreenModal, OpenFullscreenModalActions.openCreateNewRecordFullscreen),
            switchMap(() => {
                this.dialog.closeAll();
                return this.dialog.afterAllClosed.pipe(
                    take(1),
                    switchMap(() => {
                        this.dialogRef = this.dialog.open(StFullScreenDialogComponent, {
                            hasBackdrop: false,
                            disableClose: true,
                            autoFocus: true,
                            panelClass: 'st-topic-full-screen-panel',
                        });
                        const backdropObs = this.dialogRef.backdropClick().pipe(
                            tap(event => {
                                event.preventDefault();
                            }),
                            map(() => OpenFullscreenModalActions.closeFullscreenModal())
                        );
                        const keydownObs = this.dialogRef.keydownEvents().pipe(
                            filter((event: KeyboardEvent) => event.key === 'Escape'),
                            tap(event => {
                                event.preventDefault();
                            }),
                            map(() => OpenFullscreenModalActions.closeFullscreenModal())
                        );
                        return merge(keydownObs, backdropObs);
                    })
                );
            })
        );
    });

    onSidecarMenuSelected$ = createEffect(() => {
        return this.actions$.pipe(
            ofType(SidecarEfffectActions.selectSidecarMenu),
            map(action => {
                switch (action.sidecarType) {
                    case FullscreenLayoutAreas.SIDECAR_MAIN:
                        return MainSidecarActions.selectSidecarMenu({ menu: action.menu });
                    case FullscreenLayoutAreas.SIDECAR_SECOND:
                        return SecondSidecarActions.selectSidecarMenu({ menu: action.menu });
                    case FullscreenLayoutAreas.SIDECAR_THIRD:
                        return ThirdSidecarActions.selectSidecarMenu({ menu: action.menu });
                    case FullscreenLayoutAreas.SIDECAR_FOURTH:
                        return FourthSidecarActions.selectSidecarMenu({ menu: action.menu });
                    default:
                        return FullscreenActions.noOpAction();
                }
            })
        );
    });

    onAssignmentItemClicked$ = createEffect(() => {
        return this.actions$.pipe(
            ofType(OpenFullscreenModalActions.openAssignmentFullscreenDialog),
            concatLatestFrom(action => [this.store.select(selectAssignmentRecordByGuid(action.recordGuid))]),
            switchMap(([action, storeRecord]) => {
                const actions = [];
                // TODO: check open topic fullscreen dialog for setting list item guid
                actions.push(
                    OpenFullscreenModalActions.openTopicFullscreenDialog({
                        recordType: ObjectTypes.TOPIC,
                        recordGuid: storeRecord.Path.parentCollectionGuid,
                        isMetadata: action.isMetadata,
                        currentNodeUUID: action.currentNodeUUID,
                    })
                );
                actions.push(FullscreenActions.setFullscreenTemplate({ template: FullscreenLayoutTemplate.BASIC }));

                switch (action.sidecarMenu) {
                    case FullscreenLayoutAreas.SIDECAR_MAIN:
                        actions.push(MainSidecarActions.selectSidecarAssignment({ assignmentGuid: action.recordGuid }));
                        actions.push(MainSidecarActions.selectSidecarMenu({ menu: SidecarMenu.ASSIGNMENTS }));
                        break;
                    case FullscreenLayoutAreas.SIDECAR_SECOND:
                        actions.push(SecondSidecarActions.selectSidecarAssignment({ assignmentGuid: action.recordGuid }));
                        actions.push(SecondSidecarActions.selectSidecarMenu({ menu: SidecarMenu.ASSIGNMENTS }));
                        break;
                    case FullscreenLayoutAreas.SIDECAR_THIRD:
                        actions.push(ThirdSidecarActions.selectSidecarAssignment({ assignmentGuid: action.recordGuid }));
                        actions.push(ThirdSidecarActions.selectSidecarMenu({ menu: SidecarMenu.ASSIGNMENTS }));
                        break;
                    case FullscreenLayoutAreas.SIDECAR_FOURTH:
                        actions.push(FourthSidecarActions.selectSidecarAssignment({ assignmentGuid: action.recordGuid }));
                        actions.push(FourthSidecarActions.selectSidecarMenu({ menu: SidecarMenu.ASSIGNMENTS }));
                        break;
                }
                return actions;
            })
        );
    });

    openHomeItem$ = createEffect(() => {
        return this.actions$.pipe(
            ofType(FullscreenActions.openHomeItem),
            concatLatestFrom(() => [this.store.select(selectFullscreenCurrentViewRecord)]),
            map(([_, currentListRecord]) => {
                switch (currentListRecord.Type) {
                    case ObjectTypes.TOPIC:
                        return FullscreenActions.openTopic({ recordGuid: currentListRecord.Guid, recordType: currentListRecord.Type, fromPinnedItem: false });
                    case ObjectTypes.RUNDOWN:
                        return FullscreenActions.openRundown({ recordGuid: currentListRecord.Guid, recordType: currentListRecord.Type, fromPinnedItem: false });
                    case ObjectTypes.SCRIPT:
                        return FullscreenActions.openStory({ recordGuid: currentListRecord.Guid, recordType: currentListRecord.Type, fromPinnedItem: false });
                    default:
                        return FullscreenActions.noOpAction();
                }
            })
        );
    });

    openAssignment$ = createEffect(() => {
        return this.actions$.pipe(
            ofType(FullscreenActions.openAssignment),
            concatLatestFrom(() => [this.store.select(selectLayoutTemplate)]),
            exhaustMap(([action, layout]) => {
                const actions = [];
                switch (action.sidecarMenu) {
                    case FullscreenLayoutAreas.SIDECAR_MAIN:
                        actions.push(MainSidecarActions.selectSidecarAssignment({ assignmentGuid: action.recordGuid }));
                        if (action.vsGuid) {
                            actions.push(MainSidecarActions.setCurrentAssignmentViewState({ guid: action.vsGuid }));
                        }
                        break;
                    case FullscreenLayoutAreas.SIDECAR_SECOND:
                        actions.push(SecondSidecarActions.selectSidecarAssignment({ assignmentGuid: action.recordGuid }));
                        if (action.vsGuid) {
                            actions.push(SecondSidecarActions.setCurrentAssignmentViewState({ guid: action.vsGuid }));
                        }
                        break;
                    case FullscreenLayoutAreas.SIDECAR_THIRD:
                        actions.push(ThirdSidecarActions.selectSidecarAssignment({ assignmentGuid: action.recordGuid }));
                        if (action.vsGuid) {
                            actions.push(ThirdSidecarActions.setCurrentAssignmentViewState({ guid: action.vsGuid }));
                        }
                        break;
                    case FullscreenLayoutAreas.SIDECAR_FOURTH:
                        actions.push(FourthSidecarActions.selectSidecarAssignment({ assignmentGuid: action.recordGuid }));
                        if (action.vsGuid) {
                            actions.push(FourthSidecarActions.setCurrentAssignmentViewState({ guid: action.vsGuid }));
                        }
                        break;
                }
                if (actions.length > 0 && layout === FullscreenLayoutTemplate.ONLY_MAIN) {
                    actions.push(FullscreenActions.setFullscreenTemplate({ template: FullscreenLayoutTemplate.BASIC }));
                }

                if (actions.length > 0) {
                    return actions;
                }
                return of(FullscreenActions.noOpAction());
            })
        );
    });

    onResetNewRecord$ = createEffect(() => {
        return this.actions$.pipe(
            ofType(FullscreenActions.resetCurrentNewRecord),
            concatLatestFrom(() => [this.store.select(selectFullscreenNewRecordDialogState), this.store.select(selectTopicTemplatesMap), this.store.select(selectStoryTemplatesMap)]),
            switchMap(async ([action, state, topicTemplates, storyTemplates]) => {
                let newRecord = initializeRecord(action.recordType);
                switch (action.recordType) {
                    case ObjectTypes.TOPIC: {
                        const recordTemplate = topicTemplates.get(state.recordTemplateGuid);
                        if (recordTemplate) {
                            newRecord = await this.playbookService.initializeTopicFromTemplate(recordTemplate.Database, recordTemplate.Path.fullPath, recordTemplate.Guid);
                        } else {
                            this.playbookService.initializeNewTopicRecordV2(newRecord);
                        }
                        break;
                    }
                    case ObjectTypes.SCRIPT: {
                        const recordTemplate = storyTemplates.get(state.recordTemplateGuid);
                        if (recordTemplate) {
                            newRecord = await this.playbookService.initializeStoryFromTemplate(recordTemplate.Database, recordTemplate.Path.fullPath, recordTemplate.Guid);
                        } else {
                            this.playbookService.initializeNewStoryRecordV2(newRecord);
                        }
                        newRecord.ModBy = this.nomWebServicesJson.UserId;
                        // TODO: not sure we need to create story in group
                        break;
                    }
                }
                newRecord.Guid = v4();
                newRecord.isNewRecord = true;
                return FullscreenActions.updateCurrentNewRecord({ record: newRecord });
            }),
            catchError(() => {
                return of(FullscreenActions.noOpAction());
            })
        );
    });

    onInitCreateNewTopic$ = createEffect(() => {
        return this.actions$.pipe(
            ofType(OpenFullscreenModalActions.initCreateNewRecordFullscreen),
            concatLatestFrom(() => [this.store.select(selectCurrentGroupBy), this.store.select(selectFieldDefsDict)]),
            switchMap(async ([action, groupBy, fieldDefs]) => {
                let newRecord = initializeRecord(action.recordType);
                switch (action.recordType) {
                    case ObjectTypes.TOPIC: {
                        if (action.recordTemplate) {
                            newRecord = await this.playbookService.initializeTopicFromTemplate(action.recordTemplate.Database, action.recordTemplate.Path.fullPath, action.recordTemplate.Guid);
                        } else if (action.wireRecord) {
                            newRecord = this.playbookService.initializeTopicFromWire(action.wireRecord);
                        } else {
                            this.playbookService.initializeNewTopicRecordV2(newRecord);
                        }
                        if (action.groupData) {
                            const groupByField = fieldDefs[groupBy];
                            createTopicInGroup(newRecord, groupByField, action.groupData);
                        }
                        break;
                    }
                    case ObjectTypes.SCRIPT: {
                        if (action.recordTemplate) {
                            newRecord = await this.playbookService.initializeStoryFromTemplate(action.recordTemplate.Database, action.recordTemplate.Path.fullPath, action.recordTemplate.Guid);
                        } else {
                            this.playbookService.initializeNewStoryRecordV2(newRecord);
                        }
                        newRecord.ModBy = this.nomWebServicesJson.UserId;
                        // TODO: not sure we need to create story in group
                        break;
                    }
                }
                newRecord.Guid = v4();
                newRecord.isNewRecord = true;

                return OpenFullscreenModalActions.openCreateNewRecordFullscreen({
                    record: newRecord,
                    selectedStoryIndex: -1,
                    recordTemplateGuid: action.recordTemplate?.Guid,
                    createInGroupData: action.groupData,
                    recordCreating: false,
                    wireRecord: action.wireRecord,
                });
            })
        );
    });

    onOpenStory$ = createEffect(() => {
        return this.actions$.pipe(
            ofType(FullscreenActions.openStory, OpenFullscreenModalActions.openStoryFullscreenDialog),
            concatLatestFrom(() => [this.store.select(selectCurrentRecord)]),
            switchMap(([action, currentStory]) => {
                if (currentStory?.isNewRecord) {
                    return of(FullscreenActions.noOpAction());
                }

                const loadTopicStories = async (story: StorytellingStoryRecord) => {
                    if (action.type === OpenFullscreenModalActions.openStoryFullscreenDialog.type && story.PSTStoryTopicGUID) {
                        const topics = this.store.selectSignal(selectTopicRecordsMap)();
                        const topic = topics.get(story.PSTStoryTopicGUID);
                        if (!topic) {
                            return [];
                        }
                        const storiesRes = await this.topicService.getTopicStories(topic.Database, topic.Path.fullPath.substring(0, topic.Path.fullPath.lastIndexOf('\\')), topic.Guid);
                        return storiesRes.data;
                    }
                    return [];
                };

                return from(loadTopicStories(currentStory)).pipe(
                    switchMap(stories => {
                        return from(
                            this.basicContentService.getRecord<StorytellingStoryRecord>(currentStory?.Database || this.nomWebServicesJson.getDatabase(), currentStory.Path.fullPath, currentStory.Guid)
                        ).pipe(
                            switchMap(res => {
                                if (res.status) {
                                    const actions = [];
                                    stories = stories.filter(s => s.Guid !== res.data.Guid);
                                    stories.push(res.data);
                                    actions.push(StoriesRecordActions.updateStories({ records: stories }));
                                    if (action.type === OpenFullscreenModalActions.openStoryFullscreenDialog.type) {
                                        actions.push(OpenFullscreenModalActions.openFullscreenModal());
                                    }
                                    if (action.currentNodeUUID) {
                                        actions.push(ListActions.navigateObject({ nodeUuid: action.currentNodeUUID }));
                                    }
                                    return of(...actions);
                                }
                                return of(FullscreenActions.noOpAction());
                            })
                        );
                    })
                );
            })
        );
    });

    onCloseCurrentStory$ = createEffect(() => {
        return this.actions$.pipe(
            ofType(FullscreenActions.closeCurrentStory),
            concatLatestFrom(() => [this.store.select(selectStoryRecordToEdit)]),
            switchMap(([_, currentStory]) => {
                const actions = [];
                if (currentStory?.Guid) {
                    // actions.push(StoryHttpActions.releaseCurrentStoryLock());
                }
                actions.push(
                    FullscreenActions.openTopic({ recordGuid: currentStory?.PSTStoryTopicGUID, recordType: ObjectTypes.TOPIC }),
                    StoriesRecordActions.setStoryHasUnsavedChanges({ storyGuid: currentStory?.Guid, hasUnsavedChanges: false })
                );
                return of(...actions);
            })
        );
    });

    onNavigateAssignment$ = createEffect(() => {
        return this.actions$.pipe(
            ofType(FullscreenActions.navigateAssignment),
            concatLatestFrom(action => {
                return [this.store.select(selectSidecarAssignmentList(action.sidecarMenu)), this.store.select(selectSidecarSelectedAssignmentIndex(action.sidecarMenu))];
            }),
            map(([action, assignmentList, selectedIdx]) => {
                if (selectedIdx == null || selectedIdx < 0 || assignmentList?.length === 0 || selectedIdx >= assignmentList.length) {
                    return FullscreenActions.noOpAction();
                }
                let assignmentGuid = assignmentList[selectedIdx];
                switch (action.direction) {
                    case NavigationDirection.PREVIOUS:
                        if (selectedIdx === 0) {
                            assignmentGuid = assignmentList[assignmentList.length - 1];
                        } else {
                            assignmentGuid = assignmentList[selectedIdx - 1];
                        }
                        break;
                    case NavigationDirection.NEXT:
                        if (selectedIdx === assignmentList.length - 1) {
                            assignmentGuid = assignmentList[0];
                        } else {
                            assignmentGuid = assignmentList[selectedIdx + 1];
                        }
                        break;
                    case NavigationDirection.FIRST:
                        assignmentGuid = assignmentList[0];
                        break;
                    case NavigationDirection.LAST:
                        assignmentGuid = assignmentList[assignmentList.length - 1];
                        break;
                }

                return FullscreenActions.openAssignment({
                    recordGuid: assignmentGuid,
                    recordType: ObjectTypes.ASSIGNMENT,
                    sidecarMenu: action.sidecarMenu,
                });
            })
        );
    });

    onSearchTermChanged$ = createEffect(() => {
        return this.actions$.pipe(
            debounceTime(300),
            ofType(FullscreenActions.setSearchTerm),
            switchMap(({ searchTerm }) => {
                if (!searchTerm) {
                    return of(FullscreenActions.setSearchResultsGuids({ guids: [] }));
                }
                const vs: SearchViewStateRecord = new SearchViewStateRecord();
                vs.viewStateSearchQuery = searchTerm;
                return from(this.playbookSearchService.getPlaybookResults(vs)).pipe(
                    exhaustMap(res => {
                        if (res.status) {
                            const actions = [];
                            actions.push(FullscreenActions.setSearchResultsGuids({ guids: [...(res.data.listGuids?.values() || [])] }));
                            if (res.data.stories) {
                                actions.push(StoriesRecordActions.updateStories({ records: [...res.data.stories.values()] }));
                            }
                            if (res.data.topics) {
                                actions.push(TopicRecordActions.updateTopics({ records: [...res.data.topics.values()] }));
                            }
                            if (res.data.assignments) {
                                actions.push(AssignmentRecordActions.updateAssignments({ records: [...res.data.assignments.values()] }));
                            }
                            return actions;
                        }
                        return of(FullscreenActions.noOpAction());
                    })
                );
            })
        );
    });

    loadSidecarTopicViewStates$ = createEffect(() => {
        return this.actions$.pipe(
            ofType(SidecarEfffectActions.loadSidecarTopicViewStates),
            switchMap(({ sidecarType }) => {
                return from(this.viewStateService.getViewStateRecords('playbook', null, 'group')).pipe(
                    map(({ data }) => data?.views?.filter(vs => vs.viewStateType === ViewStateTypesEnum.TOPIC)),
                    exhaustMap(views => {
                        const actions = [];
                        switch (sidecarType) {
                            case FullscreenLayoutAreas.SIDECAR_MAIN:
                                actions.push(MainSidecarActions.setTopicViewStates({ viewStates: views }));
                                break;
                            case FullscreenLayoutAreas.SIDECAR_SECOND:
                                actions.push(SecondSidecarActions.setTopicViewStates({ viewStates: views }));
                                break;
                            case FullscreenLayoutAreas.SIDECAR_THIRD:
                                actions.push(ThirdSidecarActions.setTopicViewStates({ viewStates: views }));
                                break;
                            case FullscreenLayoutAreas.SIDECAR_FOURTH:
                                actions.push(FourthSidecarActions.setTopicViewStates({ viewStates: views }));
                                break;
                            default:
                                break;
                        }
                        const preselectedVS = views?.[0];
                        if (preselectedVS) {
                            actions.push(SidecarEfffectActions.setSidecarTopicCurrentViewState({ guid: preselectedVS.guid, sidecarType }));
                        }
                        if (actions.length > 0) {
                            return actions;
                        }
                        return of(FullscreenActions.noOpAction());
                    }),
                    catchError(() => of(FullscreenActions.noOpAction()))
                );
            })
        );
    });

    setSidecarTopicViewState$ = createEffect(() => {
        return this.actions$.pipe(
            ofType(SidecarEfffectActions.setSidecarTopicCurrentViewState),
            map(({ guid, sidecarType }) => {
                switch (sidecarType) {
                    case FullscreenLayoutAreas.SIDECAR_MAIN:
                        return MainSidecarActions.setCurrentTopicViewState({ guid });
                    case FullscreenLayoutAreas.SIDECAR_SECOND:
                        return SecondSidecarActions.setCurrentTopicViewState({ guid });
                    case FullscreenLayoutAreas.SIDECAR_THIRD:
                        return ThirdSidecarActions.setCurrentTopicViewState({ guid });
                    case FullscreenLayoutAreas.SIDECAR_FOURTH:
                        return FourthSidecarActions.setCurrentTopicViewState({ guid });
                    default:
                        break;
                }
                return FullscreenActions.noOpAction();
            })
        );
    });

    loadSidecarTopics$ = createEffect(() => {
        return this.actions$.pipe(
            ofType(
                MainSidecarActions.setCurrentTopicViewState,
                SecondSidecarActions.setCurrentTopicViewState,
                ThirdSidecarActions.setCurrentTopicViewState,
                FourthSidecarActions.setCurrentTopicViewState,
                MainSidecarActions.reloadTopicsBasedOnCurrentViewState,
                SecondSidecarActions.reloadTopicsBasedOnCurrentViewState,
                ThirdSidecarActions.reloadTopicsBasedOnCurrentViewState,
                FourthSidecarActions.reloadTopicsBasedOnCurrentViewState
            ),
            concatLatestFrom(() => [
                this.store.select(selectSidecarMainCurrentTopicViewState),
                this.store.select(selectSidecarSecondCurrentTopicViewState),
                this.store.select(selectSidecarThirdCurrentTopicViewState),
                this.store.select(selectSidecarFourthCurrentTopicViewState),
            ]),
            switchMap(([action, main, second, third, fourth]) => {
                let viewState = null;
                let sidecarType = null;
                switch (action.type) {
                    case MainSidecarActions.setCurrentTopicViewState.type:
                    case MainSidecarActions.reloadTopicsBasedOnCurrentViewState.type:
                        viewState = main;
                        sidecarType = FullscreenLayoutAreas.SIDECAR_MAIN;
                        break;

                    case SecondSidecarActions.setCurrentTopicViewState.type:
                    case SecondSidecarActions.reloadTopicsBasedOnCurrentViewState.type:
                        viewState = second;
                        sidecarType = FullscreenLayoutAreas.SIDECAR_SECOND;
                        break;

                    case ThirdSidecarActions.setCurrentTopicViewState.type:
                    case ThirdSidecarActions.reloadTopicsBasedOnCurrentViewState.type:
                        viewState = third;
                        sidecarType = FullscreenLayoutAreas.SIDECAR_THIRD;
                        break;

                    case FourthSidecarActions.setCurrentTopicViewState.type:
                    case FourthSidecarActions.reloadTopicsBasedOnCurrentViewState.type:
                        viewState = fourth;
                        sidecarType = FullscreenLayoutAreas.SIDECAR_FOURTH;
                        break;

                    default:
                        break;
                }
                if (viewState?.guid) {
                    return this.loadSidecarTopicsByVS(sidecarType, viewState);
                }
                return of(FullscreenActions.noOpAction());
            })
        );
    });

    reloadSidecarTopics$ = createEffect(() => {
        return this.actions$.pipe(
            ofType(SidecarEfffectActions.reloadSidecarTopicsBasedOnCurrentViewState),
            map(action => {
                switch (action.sidecarType) {
                    case FullscreenLayoutAreas.SIDECAR_MAIN:
                        return MainSidecarActions.reloadTopicsBasedOnCurrentViewState();
                    case FullscreenLayoutAreas.SIDECAR_SECOND:
                        return SecondSidecarActions.reloadTopicsBasedOnCurrentViewState();
                    case FullscreenLayoutAreas.SIDECAR_THIRD:
                        return ThirdSidecarActions.reloadTopicsBasedOnCurrentViewState();
                    case FullscreenLayoutAreas.SIDECAR_FOURTH:
                        return FourthSidecarActions.reloadTopicsBasedOnCurrentViewState();
                    default:
                        return FullscreenActions.noOpAction();
                }
            })
        );
    });

    onSidecarTopicSearchTermChanged$ = createEffect(() => {
        return this.actions$.pipe(
            debounceTime(300),
            ofType(SidecarEfffectActions.searchTopics),
            switchMap(({ sidecarType, searchTerm }) => {
                if (!searchTerm) {
                    return of(SidecarEfffectActions.clearSearchTopics({ sidecarType }));
                }
                const vs: SearchViewStateRecord = new SearchViewStateRecord();
                vs.viewStateSearchQuery = searchTerm;
                vs.viewStateType = ViewStateTypesEnum.TOPIC;
                return this.loadSidecarTopicsByVS(sidecarType, vs);
            })
        );
    });

    clearSidecarTopicSearch$ = createEffect(() => {
        return this.actions$.pipe(
            ofType(SidecarEfffectActions.clearSearchTopics),
            map(action => {
                switch (action.sidecarType) {
                    case FullscreenLayoutAreas.SIDECAR_MAIN:
                        return MainSidecarActions.setTopicListGuids({ topicGuids: [] });
                    case FullscreenLayoutAreas.SIDECAR_SECOND:
                        return SecondSidecarActions.setTopicListGuids({ topicGuids: [] });
                    case FullscreenLayoutAreas.SIDECAR_THIRD:
                        return ThirdSidecarActions.setTopicListGuids({ topicGuids: [] });
                    case FullscreenLayoutAreas.SIDECAR_FOURTH:
                        return FourthSidecarActions.setTopicListGuids({ topicGuids: [] });
                    default:
                        return FullscreenActions.noOpAction();
                }
            })
        );
    });

    navigateToRecord$ = createEffect(() => {
        return this.actions$.pipe(
            ofType(FullscreenActions.navigateRecord),
            concatLatestFrom(() => [this.store.select(selectCurrentNodeUuid), this.store.select(selectTreeControl), this.store.select(selectCurrentViewState)]),
            switchMap(([action, currentNodeUUID, treecontrol, vs]) => {
                if (!action.throughTopicStories) {
                    const lvl = extractLowestNodeLevel(treecontrol.dataNodes);
                    const nextItem = navigateTo(action.direction, currentNodeUUID, treecontrol, vs, lvl);
                    return this.goToItem(nextItem);
                } else {
                    const topic = this.store.selectSignal(selectCurrentStoryParentTopic)();
                    const currentStoryIndex = this.store.selectSignal(selectCurrentStoryIndex)();
                    const newItem = navigateToStory(action.direction, currentStoryIndex, topic.stories);
                    return this.saveChanges(newItem?.Guid);
                }
            })
        );
    });

    navigateRecordDirectly$ = createEffect(() => {
        return this.actions$.pipe(
            ofType(FullscreenActions.navigateRecordDirectly),
            concatLatestFrom(() => [this.store.select(selectTreeControl)]),
            exhaustMap(([action, treeControl]) => {
                if (!action.throughTopicStories) {
                    const node = navigateDirectly(action.uniqueNodeId, treeControl);
                    return this.goToItem(node);
                } else {
                    return this.saveChanges(action.uniqueNodeId);
                }
            })
        );
    });

    goToItem(nextItem: TreeLikeFlatNode<unknown>) {
        const languageSettings = this.store.selectSignal(selectI18N)();
        const vs = this.store.selectSignal(selectCurrentViewState)();
        const hasUnsavedChanges = this.store.selectSignal(selectAnyUnsavedChanges)();
        const localNavigateToRecord = (item: TreeLikeFlatNode<unknown>) => {
            let nextItemRecord = item?.object as StorytellingRecord;
            if (vs instanceof PublicationViewStateRecord && nextItemRecord?.linkedRecords?.length > 0) {
                nextItemRecord = nextItemRecord.linkedRecords[0];
            }
            if (nextItemRecord) {
                const actions = [];
                actions.push(ListActions.navigateObject({ nodeUuid: item.uniqueId }));
                const currentTemplate = this.store.selectSignal(selectLayoutTemplate)();
                switch (nextItemRecord.Type) {
                    case ObjectTypes.TOPIC:
                        actions.push(FullscreenActions.openTopic({ recordGuid: nextItemRecord.Guid, recordType: ObjectTypes.TOPIC }));
                        // BIG TODO: A lot of edge cases where we don't have main sidecar opened, or assigments are opened in some other sidecar, or we have stories opened, etc...
                        if (currentTemplate !== FullscreenLayoutTemplate.ONLY_MAIN) {
                            const assignment = (nextItemRecord as StorytellingTopicRecord).children?.[0];
                            actions.push(MainSidecarActions.selectSidecarAssignment({ assignmentGuid: assignment?.Guid }));
                        }
                        break;
                    case ObjectTypes.ASSIGNMENT: {
                        actions.push(FullscreenActions.openTopic({ recordGuid: nextItemRecord.Path.parentCollectionGuid, recordType: ObjectTypes.TOPIC, isMetadata: true }));
                        actions.push(MainSidecarActions.selectSidecarAssignment({ assignmentGuid: nextItemRecord.Guid }));
                        actions.push(MainSidecarActions.selectSidecarMenu({ menu: SidecarMenu.ASSIGNMENTS }));
                        if (currentTemplate === FullscreenLayoutTemplate.ONLY_MAIN) {
                            actions.push(FullscreenActions.setFullscreenTemplate({ template: FullscreenLayoutTemplate.BASIC }));
                        }
                        break;
                    }
                    case ObjectTypes.SCRIPT:
                        actions.push(FullscreenActions.openStory({ recordGuid: nextItemRecord.Guid, recordType: ObjectTypes.SCRIPT }));
                        break;
                    case ObjectTypes.RUNDOWN:
                        actions.push(FullscreenActions.openRundown({ recordGuid: nextItemRecord.Guid, recordType: ObjectTypes.RUNDOWN }));
                        break;
                    default:
                        break;
                }
                return actions;
            }
            return of(FullscreenActions.noOpAction());
        };
        if (hasUnsavedChanges) {
            const modalRef = this.modalService.openModal(
                languageSettings.PSTWebConformCancelShort,
                languageSettings.PSTWebConfirmCancel,
                languageSettings.PSTWebYesOption,
                languageSettings.PSTWebNoOption
            );
            return modalRef.result.pipe(
                exhaustMap(modalRes => {
                    if (modalRes === ModalResponseCodes.OK) {
                        return localNavigateToRecord(nextItem);
                    } else {
                        return of(FullscreenActions.noOpAction());
                    }
                }),
                catchError(() => {
                    return of(FullscreenActions.noOpAction());
                })
            );
        }
        return localNavigateToRecord(nextItem);
    }

    saveChanges(nextItemGuid: string) {
        const actions = [];
        const languageSettings = this.store.selectSignal(selectI18N)();
        const hasUnsavedChanges = this.store.selectSignal(selectAnyUnsavedChanges)();
        const storyRecord = this.store.selectSignal(selectCurrentRecord)() as StorytellingStoryRecord;
        if (!nextItemGuid) {
            return of(FullscreenActions.noOpAction());
        }
        if (hasUnsavedChanges) {
            const modalRef = this.modalService.openConfirmation({
                title: languageSettings.PSTWebConfirmSaveShort,
                message: languageSettings.PSTWebConfirmSave,
                yesLabel: languageSettings.PSTWebSaveOption,
                noLabel: languageSettings.PSTWebDiscardOption,
                cancelLabel: languageSettings.PSTWebCancelButtonLabel,
            });
            return modalRef.result.pipe(
                exhaustMap(modalRes => {
                    if (modalRes === ModalResponseCodes.OK) {
                        actions.push(
                            RecordHttpActions.saveRecordChanges({
                                record: storyRecord,
                                fieldsToUpdate: [
                                    { key: PSTFieldNames.Type, value: ObjectTypes.SCRIPT },
                                    { key: PSTFieldNames.PSTRichText, value: storyRecord?.text },
                                    ...(storyRecord?.TextCommands ? [{ key: PSTFieldNames.TextCommands, value: storyRecord?.TextCommands }] : []),
                                ],
                            })
                        );
                        actions.push(FullscreenActions.openStory({ recordGuid: nextItemGuid, recordType: ObjectTypes.SCRIPT }));
                        return actions;
                    } else if (modalRes === ModalResponseCodes.DISCARD) {
                        actions.push(StoriesRecordActions.setStoryHasUnsavedChanges({ storyGuid: storyRecord.Guid, hasUnsavedChanges: false }));
                        actions.push(FullscreenActions.openStory({ recordGuid: nextItemGuid, recordType: ObjectTypes.SCRIPT }));
                        return actions;
                    } else {
                        return of(FullscreenActions.noOpAction());
                    }
                }),
                catchError(() => {
                    return of(FullscreenActions.noOpAction());
                })
            );
        }
        return of(FullscreenActions.openStory({ recordGuid: nextItemGuid, recordType: ObjectTypes.SCRIPT }));
    }

    loadSidecarTopicsByVS(sidecarType: FullscreenLayoutAreas, viewState: SearchViewStateRecord) {
        return from(this.playbookSearchService.getPlaybookResults(viewState)).pipe(
            map(data => data?.data),
            exhaustMap(results => {
                const actions = [];
                switch (sidecarType) {
                    case FullscreenLayoutAreas.SIDECAR_MAIN:
                        actions.push(MainSidecarActions.setTopicListGuids({ topicGuids: [...(results?.listGuids || [])] }));
                        break;

                    case FullscreenLayoutAreas.SIDECAR_SECOND:
                        actions.push(SecondSidecarActions.setTopicListGuids({ topicGuids: [...(results?.listGuids || [])] }));
                        break;

                    case FullscreenLayoutAreas.SIDECAR_THIRD:
                        actions.push(ThirdSidecarActions.setTopicListGuids({ topicGuids: [...(results?.listGuids || [])] }));
                        break;

                    case FullscreenLayoutAreas.SIDECAR_FOURTH:
                        actions.push(FourthSidecarActions.setTopicListGuids({ topicGuids: [...(results?.listGuids || [])] }));
                        break;

                    default:
                        break;
                }
                if (results?.stories) {
                    actions.push(StoriesRecordActions.updateStories({ records: [...(results.stories.values() || [])] }));
                }
                if (results?.topics) {
                    actions.push(TopicRecordActions.updateTopics({ records: [...(results.topics?.values() || [])] }));
                }
                if (results?.assignments) {
                    actions.push(AssignmentRecordActions.updateAssignments({ records: [...(results.assignments?.values() || [])] }));
                }
                return actions;
            }),
            catchError(() => of(FullscreenActions.noOpAction()))
        );
    }

    loadSidecarRundownViewStates$ = createEffect(() => {
        return this.actions$.pipe(
            ofType(SidecarEfffectActions.loadSidecarRundownViewStates),
            switchMap(({ sidecarType }) => {
                return from(this.viewStateService.getViewStateRecords('live', null, 'group')).pipe(
                    map(({ data }) => data?.views?.filter(vs => vs.viewStateType === ViewStateTypesEnum.ROLIST)),
                    exhaustMap(views => {
                        const actions = [];
                        switch (sidecarType) {
                            case FullscreenLayoutAreas.SIDECAR_MAIN:
                                actions.push(MainSidecarActions.setRundownViewStates({ viewStates: views }));
                                break;
                            case FullscreenLayoutAreas.SIDECAR_SECOND:
                                actions.push(SecondSidecarActions.setRundownViewStates({ viewStates: views }));
                                break;
                            case FullscreenLayoutAreas.SIDECAR_THIRD:
                                actions.push(ThirdSidecarActions.setRundownViewStates({ viewStates: views }));
                                break;
                            case FullscreenLayoutAreas.SIDECAR_FOURTH:
                                actions.push(FourthSidecarActions.setRundownViewStates({ viewStates: views }));
                                break;
                            default:
                                break;
                        }
                        const preselectedVS = views?.[0];
                        if (preselectedVS) {
                            actions.push(SidecarEfffectActions.setSidecarRundownCurrentViewState({ guid: preselectedVS.guid, sidecarType }));
                        }
                        if (actions.length > 0) {
                            return actions;
                        }
                        return of(FullscreenActions.noOpAction());
                    }),
                    catchError(() => of(FullscreenActions.noOpAction()))
                );
            })
        );
    });

    setSidecarRundownViewState$ = createEffect(() => {
        return this.actions$.pipe(
            ofType(SidecarEfffectActions.setSidecarRundownCurrentViewState),
            map(({ guid, sidecarType }) => {
                switch (sidecarType) {
                    case FullscreenLayoutAreas.SIDECAR_MAIN:
                        return MainSidecarActions.setCurrentRundownViewState({ guid });
                    case FullscreenLayoutAreas.SIDECAR_SECOND:
                        return SecondSidecarActions.setCurrentRundownViewState({ guid });
                    case FullscreenLayoutAreas.SIDECAR_THIRD:
                        return ThirdSidecarActions.setCurrentRundownViewState({ guid });
                    case FullscreenLayoutAreas.SIDECAR_FOURTH:
                        return FourthSidecarActions.setCurrentRundownViewState({ guid });
                    default:
                        break;
                }
                return FullscreenActions.noOpAction();
            })
        );
    });

    loadSidecarRundowns$ = createEffect(() => {
        return this.actions$.pipe(
            ofType(
                MainSidecarActions.setCurrentRundownViewState,
                SecondSidecarActions.setCurrentRundownViewState,
                ThirdSidecarActions.setCurrentRundownViewState,
                FourthSidecarActions.setCurrentRundownViewState,
                MainSidecarActions.reloadRundownsBasedOnCurrentViewState,
                SecondSidecarActions.reloadRundownsBasedOnCurrentViewState,
                ThirdSidecarActions.reloadRundownsBasedOnCurrentViewState,
                FourthSidecarActions.reloadRundownsBasedOnCurrentViewState
            ),
            concatLatestFrom(() => [
                this.store.select(selectSidecarMainCurrentRundownViewState),
                this.store.select(selectSidecarSecondCurrentRundownViewState),
                this.store.select(selectSidecarThirdCurrentRundownViewState),
                this.store.select(selectSidecarFourthCurrentRundownViewState),
            ]),
            switchMap(([action, main, second, third, fourth]) => {
                let viewState = null;
                let sidecarType = null;
                switch (action.type) {
                    case MainSidecarActions.setCurrentRundownViewState.type:
                    case MainSidecarActions.reloadRundownsBasedOnCurrentViewState.type:
                        viewState = main;
                        sidecarType = FullscreenLayoutAreas.SIDECAR_MAIN;
                        break;

                    case SecondSidecarActions.setCurrentRundownViewState.type:
                    case SecondSidecarActions.reloadRundownsBasedOnCurrentViewState.type:
                        viewState = second;
                        sidecarType = FullscreenLayoutAreas.SIDECAR_SECOND;
                        break;

                    case ThirdSidecarActions.setCurrentRundownViewState.type:
                    case ThirdSidecarActions.reloadRundownsBasedOnCurrentViewState.type:
                        viewState = third;
                        sidecarType = FullscreenLayoutAreas.SIDECAR_THIRD;
                        break;

                    case FourthSidecarActions.setCurrentRundownViewState.type:
                    case FourthSidecarActions.reloadRundownsBasedOnCurrentViewState.type:
                        viewState = fourth;
                        sidecarType = FullscreenLayoutAreas.SIDECAR_FOURTH;
                        break;

                    default:
                        break;
                }
                if (viewState?.guid) {
                    return this.loadSidecarRundownsByVS(sidecarType, viewState);
                }
                return of(FullscreenActions.noOpAction());
            })
        );
    });

    reloadSidecarRundowns$ = createEffect(() => {
        return this.actions$.pipe(
            ofType(SidecarEfffectActions.reloadSidecarRundownsBasedOnCurrentViewState),
            map(action => {
                switch (action.sidecarType) {
                    case FullscreenLayoutAreas.SIDECAR_MAIN:
                        return MainSidecarActions.reloadRundownsBasedOnCurrentViewState();
                    case FullscreenLayoutAreas.SIDECAR_SECOND:
                        return SecondSidecarActions.reloadRundownsBasedOnCurrentViewState();
                    case FullscreenLayoutAreas.SIDECAR_THIRD:
                        return ThirdSidecarActions.reloadRundownsBasedOnCurrentViewState();
                    case FullscreenLayoutAreas.SIDECAR_FOURTH:
                        return FourthSidecarActions.reloadRundownsBasedOnCurrentViewState();
                    default:
                        return FullscreenActions.noOpAction();
                }
            })
        );
    });

    onSidecarRundownSearchTermChanged$ = createEffect(() => {
        return this.actions$.pipe(
            debounceTime(300),
            ofType(SidecarEfffectActions.searchRundowns),
            switchMap(({ sidecarType, searchTerm }) => {
                if (!searchTerm) {
                    return of(SidecarEfffectActions.clearSearchRundowns({ sidecarType }));
                }
                const vs: SearchViewStateRecord = new SearchViewStateRecord();
                vs.viewStateSearchQuery = searchTerm;
                vs.viewStateType = ViewStateTypesEnum.ROLIST;
                return this.loadSidecarRundownsByVS(sidecarType, vs);
            })
        );
    });

    clearSidecarRundownSearch$ = createEffect(() => {
        return this.actions$.pipe(
            ofType(SidecarEfffectActions.clearSearchRundowns),
            map(action => {
                switch (action.sidecarType) {
                    case FullscreenLayoutAreas.SIDECAR_MAIN:
                        return MainSidecarActions.setRundownListGuids({ rundownGuids: [] });
                    case FullscreenLayoutAreas.SIDECAR_SECOND:
                        return SecondSidecarActions.setRundownListGuids({ rundownGuids: [] });
                    case FullscreenLayoutAreas.SIDECAR_THIRD:
                        return ThirdSidecarActions.setRundownListGuids({ rundownGuids: [] });
                    case FullscreenLayoutAreas.SIDECAR_FOURTH:
                        return FourthSidecarActions.setRundownListGuids({ rundownGuids: [] });
                    default:
                        return FullscreenActions.noOpAction();
                }
            })
        );
    });

    openCurrentRundown$ = createEffect(() => {
        return this.actions$.pipe(
            ofType(SidecarEfffectActions.openCurrentRundown),
            concatLatestFrom(() => [this.store.select(selectRundownRecordsMap)]),
            switchMap(([{ recordGuid, sidecarMenu }, rundowns]) => {
                const storeRecord = rundowns.get(recordGuid);
                if (!storeRecord) {
                    return of(FullscreenActions.noOpAction());
                }
                return from(this.rundownService.getRundown(storeRecord.Database, storeRecord.Path.fullPath, storeRecord.Guid)).pipe(
                    filter(res => res.status),
                    map(res => res.data),
                    exhaustMap(ro => {
                        const actions = [];
                        actions.push(RundownRecordActions.updateRundowns({ records: [ro] }));
                        actions.push(StoriesRecordActions.updateStories({ records: (ro as StorytellingRundownRecord).children }));
                        switch (sidecarMenu) {
                            case FullscreenLayoutAreas.SIDECAR_MAIN:
                                actions.push(MainSidecarActions.selectSidecarRundown({ guid: recordGuid }));
                                break;
                            case FullscreenLayoutAreas.SIDECAR_SECOND:
                                actions.push(SecondSidecarActions.selectSidecarRundown({ guid: recordGuid }));
                                break;
                            case FullscreenLayoutAreas.SIDECAR_THIRD:
                                actions.push(ThirdSidecarActions.selectSidecarRundown({ guid: recordGuid }));
                                break;
                            case FullscreenLayoutAreas.SIDECAR_FOURTH:
                                actions.push(FourthSidecarActions.selectSidecarRundown({ guid: recordGuid }));
                                break;
                        }

                        if (actions.length > 0) {
                            return actions;
                        }
                        return of(FullscreenActions.noOpAction());
                    }),
                    catchError(() => of(FullscreenActions.noOpAction()))
                );
            })
        );
    });

    clearSelectedRundown$ = createEffect(() => {
        return this.actions$.pipe(
            ofType(SidecarEfffectActions.clearSelectedRundown),
            map(action => {
                switch (action.sidecarType) {
                    case FullscreenLayoutAreas.SIDECAR_MAIN:
                        return MainSidecarActions.selectSidecarRundown({ guid: null });
                    case FullscreenLayoutAreas.SIDECAR_SECOND:
                        return SecondSidecarActions.selectSidecarRundown({ guid: null });
                    case FullscreenLayoutAreas.SIDECAR_THIRD:
                        return ThirdSidecarActions.selectSidecarRundown({ guid: null });
                    case FullscreenLayoutAreas.SIDECAR_FOURTH:
                        return FourthSidecarActions.selectSidecarRundown({ guid: null });
                    default:
                        return FullscreenActions.noOpAction();
                }
            })
        );
    });

    loadSidecarRundownsByVS(sidecarType: FullscreenLayoutAreas, viewState: SearchViewStateRecord) {
        if (viewState instanceof SingleViewStateRecord) {
            const databases = getDatabasesOptions(
                viewState,
                this.nomWebServicesJson.PSTServer,
                this.nomWebServicesJson.HelloResults.HomeProgram,
                this.globalTablesService.AllGroups,
                this.globalTablesService.AllGroupsArray
            );

            return forkJoin(
                databases.map(database => {
                    return from(this.rundownService.loadRundownsV2(viewState, database));
                })
            ).pipe(
                exhaustMap(response => {
                    let rundowns = new Map<string, StorytellingRundownRecord>();
                    let stories = new Map<string, StorytellingStoryRecord>();
                    let listGuids = new Set<string>();
                    response.forEach(res => {
                        if (res.status) {
                            rundowns = new Map([...rundowns, ...res.data.rundowns]);
                            stories = new Map([...stories, ...res.data.stories]);
                            listGuids = new Set([...listGuids, ...res.data.listGuids]);
                        }
                    });

                    const actions = [];
                    const listRundownGuids = Array.from(listGuids);
                    switch (sidecarType) {
                        case FullscreenLayoutAreas.SIDECAR_MAIN:
                            actions.push(MainSidecarActions.setRundownListGuids({ rundownGuids: [...(listRundownGuids || [])] }));
                            break;

                        case FullscreenLayoutAreas.SIDECAR_SECOND:
                            actions.push(SecondSidecarActions.setRundownListGuids({ rundownGuids: [...(listRundownGuids || [])] }));
                            break;

                        case FullscreenLayoutAreas.SIDECAR_THIRD:
                            actions.push(ThirdSidecarActions.setRundownListGuids({ rundownGuids: [...(listRundownGuids || [])] }));
                            break;

                        case FullscreenLayoutAreas.SIDECAR_FOURTH:
                            actions.push(FourthSidecarActions.setRundownListGuids({ rundownGuids: [...(listRundownGuids || [])] }));
                            break;

                        default:
                            break;
                    }

                    if (stories) {
                        actions.push(StoriesRecordActions.updateStories({ records: [...(stories.values() || [])] }));
                    }
                    if (rundowns) {
                        actions.push(RundownRecordActions.updateRundowns({ records: [...(rundowns?.values() || [])] }));
                    }
                    return actions;
                }),
                catchError(err => {
                    console.error(err);
                    return of(FullscreenActions.noOpAction());
                })
            );
        }
    }

    // assignment sidecar effects
    loadSidecarAssignmentViewStates$ = createEffect(() => {
        return this.actions$.pipe(
            ofType(SidecarEfffectActions.loadSidecarAssignmentViewStates),
            concatLatestFrom(() => [this.store.select(selectCurrentRecordType)]),
            switchMap(([{ sidecarType }, currentRecordType]) => {
                return from(this.viewStateService.getViewStateRecords('playbook', null, 'group')).pipe(
                    map(({ data }) => data?.views?.filter(vs => vs.viewStateType === ViewStateTypesEnum.ASSIGNMENT)),
                    exhaustMap(viewsData => {
                        const actions = [];
                        let views = [...viewsData];
                        if (currentRecordType !== ObjectTypes.RUNDOWN) {
                            const systemVs = new ViewStateRecord();
                            systemVs.guid = SYSTEM_VS_GUID;
                            systemVs.viewStateType = ViewStateTypesEnum.ASSIGNMENT;
                            systemVs.title = SYSTEM_VS_NAME_TOPIC;
                            views = [systemVs, ...views];
                        }

                        switch (sidecarType) {
                            case FullscreenLayoutAreas.SIDECAR_MAIN:
                                actions.push(MainSidecarActions.setAssignmentViewStates({ viewStates: views }));
                                break;
                            case FullscreenLayoutAreas.SIDECAR_SECOND:
                                actions.push(SecondSidecarActions.setAssignmentViewStates({ viewStates: views }));
                                break;
                            case FullscreenLayoutAreas.SIDECAR_THIRD:
                                actions.push(ThirdSidecarActions.setAssignmentViewStates({ viewStates: views }));
                                break;
                            case FullscreenLayoutAreas.SIDECAR_FOURTH:
                                actions.push(FourthSidecarActions.setAssignmentViewStates({ viewStates: views }));
                                break;
                            default:
                                break;
                        }
                        const preselectedVS = views?.[0];
                        if (preselectedVS) {
                            actions.push(SidecarEfffectActions.setSidecarAssignmentCurrentViewState({ guid: preselectedVS.guid, sidecarType }));
                        }
                        if (actions.length > 0) {
                            return actions;
                        }
                        return of(FullscreenActions.noOpAction());
                    }),
                    catchError(() => of(FullscreenActions.noOpAction()))
                );
            })
        );
    });

    setSidecarAssignmentViewState$ = createEffect(() => {
        return this.actions$.pipe(
            ofType(SidecarEfffectActions.setSidecarAssignmentCurrentViewState),
            map(({ guid, sidecarType }) => {
                switch (sidecarType) {
                    case FullscreenLayoutAreas.SIDECAR_MAIN:
                        return MainSidecarActions.setCurrentAssignmentViewState({ guid });
                    case FullscreenLayoutAreas.SIDECAR_SECOND:
                        return SecondSidecarActions.setCurrentAssignmentViewState({ guid });
                    case FullscreenLayoutAreas.SIDECAR_THIRD:
                        return ThirdSidecarActions.setCurrentAssignmentViewState({ guid });
                    case FullscreenLayoutAreas.SIDECAR_FOURTH:
                        return FourthSidecarActions.setCurrentAssignmentViewState({ guid });
                    default:
                        break;
                }
                return FullscreenActions.noOpAction();
            })
        );
    });

    loadSidecarAssignments$ = createEffect(() => {
        return this.actions$.pipe(
            ofType(
                MainSidecarActions.setCurrentAssignmentViewState,
                SecondSidecarActions.setCurrentAssignmentViewState,
                ThirdSidecarActions.setCurrentAssignmentViewState,
                FourthSidecarActions.setCurrentAssignmentViewState,
                MainSidecarActions.reloadAssignmentBasedOnCurrentViewState,
                SecondSidecarActions.reloadAssignmentBasedOnCurrentViewState,
                ThirdSidecarActions.reloadAssignmentBasedOnCurrentViewState,
                FourthSidecarActions.reloadAssignmentBasedOnCurrentViewState
            ),
            concatLatestFrom(() => [
                this.store.select(selectSidecarMainCurrentAssignmentViewState),
                this.store.select(selectSidecarSecondCurrentAssignmentViewState),
                this.store.select(selectSidecarThirdCurrentAssignmentViewState),
                this.store.select(selectSidecarFourthCurrentAssignmentViewState),
            ]),
            switchMap(([action, main, second, third, fourth]) => {
                let viewState = null;
                let sidecarType = null;
                switch (action.type) {
                    case MainSidecarActions.setCurrentAssignmentViewState.type:
                    case MainSidecarActions.reloadAssignmentBasedOnCurrentViewState.type:
                        viewState = main;
                        sidecarType = FullscreenLayoutAreas.SIDECAR_MAIN;
                        break;

                    case SecondSidecarActions.setCurrentAssignmentViewState.type:
                    case SecondSidecarActions.reloadAssignmentBasedOnCurrentViewState.type:
                        viewState = second;
                        sidecarType = FullscreenLayoutAreas.SIDECAR_SECOND;
                        break;

                    case ThirdSidecarActions.setCurrentAssignmentViewState.type:
                    case ThirdSidecarActions.reloadAssignmentBasedOnCurrentViewState.type:
                        viewState = third;
                        sidecarType = FullscreenLayoutAreas.SIDECAR_THIRD;
                        break;

                    case FourthSidecarActions.setCurrentAssignmentViewState.type:
                    case FourthSidecarActions.reloadAssignmentBasedOnCurrentViewState.type:
                        viewState = fourth;
                        sidecarType = FullscreenLayoutAreas.SIDECAR_FOURTH;
                        break;

                    default:
                        break;
                }
                if (viewState?.guid) {
                    return this.loadSidecarAssignmentsByVS(sidecarType, viewState);
                }
                return of(FullscreenActions.noOpAction());
            })
        );
    });

    reloadSidecarAssignments$ = createEffect(() => {
        return this.actions$.pipe(
            ofType(SidecarEfffectActions.reloadSidecarAssignmentBasedOnCurrentViewState),
            map(action => {
                switch (action.sidecarType) {
                    case FullscreenLayoutAreas.SIDECAR_MAIN:
                        return MainSidecarActions.reloadAssignmentBasedOnCurrentViewState();
                    case FullscreenLayoutAreas.SIDECAR_SECOND:
                        return SecondSidecarActions.reloadAssignmentBasedOnCurrentViewState();
                    case FullscreenLayoutAreas.SIDECAR_THIRD:
                        return ThirdSidecarActions.reloadAssignmentBasedOnCurrentViewState();
                    case FullscreenLayoutAreas.SIDECAR_FOURTH:
                        return FourthSidecarActions.reloadAssignmentBasedOnCurrentViewState();
                    default:
                        return FullscreenActions.noOpAction();
                }
            })
        );
    });

    onSidecarAssignmentSearchTermChanged$ = createEffect(() => {
        return this.actions$.pipe(
            debounceTime(300),
            ofType(SidecarEfffectActions.searchAssignments),
            switchMap(({ sidecarType, searchTerm }) => {
                if (!searchTerm) {
                    return of(SidecarEfffectActions.clearSearchAssignment({ sidecarType }));
                }
                const vs: SearchViewStateRecord = new SearchViewStateRecord();
                vs.viewStateSearchQuery = searchTerm;
                vs.viewStateType = ViewStateTypesEnum.ASSIGNMENT;
                return this.loadSidecarAssignmentsByVS(sidecarType, vs);
            })
        );
    });

    clearSidecarAssignmentSearch$ = createEffect(() => {
        return this.actions$.pipe(
            ofType(SidecarEfffectActions.clearSearchAssignment),
            map(action => {
                switch (action.sidecarType) {
                    case FullscreenLayoutAreas.SIDECAR_MAIN:
                        return MainSidecarActions.setAssignmentListGuids({ assignmentGuids: [] });
                    case FullscreenLayoutAreas.SIDECAR_SECOND:
                        return SecondSidecarActions.setAssignmentListGuids({ assignmentGuids: [] });
                    case FullscreenLayoutAreas.SIDECAR_THIRD:
                        return ThirdSidecarActions.setAssignmentListGuids({ assignmentGuids: [] });
                    case FullscreenLayoutAreas.SIDECAR_FOURTH:
                        return FourthSidecarActions.setAssignmentListGuids({ assignmentGuids: [] });
                    default:
                        return FullscreenActions.noOpAction();
                }
            })
        );
    });

    clearSelectedAssignment$ = createEffect(() => {
        return this.actions$.pipe(
            ofType(SidecarEfffectActions.clearSelectedAssignment),
            map(action => {
                switch (action.sidecarType) {
                    case FullscreenLayoutAreas.SIDECAR_MAIN:
                        return MainSidecarActions.selectSidecarAssignment({ assignmentGuid: null });
                    case FullscreenLayoutAreas.SIDECAR_SECOND:
                        return SecondSidecarActions.selectSidecarAssignment({ assignmentGuid: null });
                    case FullscreenLayoutAreas.SIDECAR_THIRD:
                        return ThirdSidecarActions.selectSidecarAssignment({ assignmentGuid: null });
                    case FullscreenLayoutAreas.SIDECAR_FOURTH:
                        return FourthSidecarActions.selectSidecarAssignment({ assignmentGuid: null });
                    default:
                        return FullscreenActions.noOpAction();
                }
            })
        );
    });

    setCurrentTopicAssignments$ = createEffect(() => {
        return this.actions$.pipe(
            ofType(SidecarEfffectActions.setCurrentTopicAssignments),
            concatLatestFrom(() => [this.store.select(selectCurrentRecord), this.store.select(selectCurrentStoryParentTopic)]),
            map(([action, currentRecord, storyParentTopic]) => {
                const record = currentRecord?.Type === ObjectTypes.TOPIC ? currentRecord : storyParentTopic;
                switch (action.sidecarType) {
                    case FullscreenLayoutAreas.SIDECAR_MAIN:
                        return MainSidecarActions.setAssignmentListGuids({ assignmentGuids: [...((record as StorytellingTopicRecord)?.Sequence || [])] });
                    case FullscreenLayoutAreas.SIDECAR_SECOND:
                        return SecondSidecarActions.setAssignmentListGuids({ assignmentGuids: [...((record as StorytellingTopicRecord)?.Sequence || [])] });
                    case FullscreenLayoutAreas.SIDECAR_THIRD:
                        return ThirdSidecarActions.setAssignmentListGuids({ assignmentGuids: [...((record as StorytellingTopicRecord)?.Sequence || [])] });
                    case FullscreenLayoutAreas.SIDECAR_FOURTH:
                        return FourthSidecarActions.setAssignmentListGuids({ assignmentGuids: [...((record as StorytellingTopicRecord)?.Sequence || [])] });
                }
                return FullscreenActions.noOpAction();
            })
        );
    });

    setAssignmentsListSearchType$ = createEffect(() => {
        return this.actions$.pipe(
            ofType(SidecarEfffectActions.setAssignmentsListSearchType),
            map(({ sidecarType, searchType }) => {
                if (!Object.values(SearchType).includes(searchType)) {
                    return FullscreenActions.noOpAction();
                }
                switch (sidecarType) {
                    case FullscreenLayoutAreas.SIDECAR_MAIN:
                        return MainSidecarActions.setAssignmentsListSearchType({ searchType });
                    case FullscreenLayoutAreas.SIDECAR_SECOND:
                        return SecondSidecarActions.setAssignmentsListSearchType({ searchType });
                    case FullscreenLayoutAreas.SIDECAR_THIRD:
                        return ThirdSidecarActions.setAssignmentsListSearchType({ searchType });
                    case FullscreenLayoutAreas.SIDECAR_FOURTH:
                        return FourthSidecarActions.setAssignmentsListSearchType({ searchType });
                }
                return FullscreenActions.noOpAction();
            })
        );
    });

    setStoriesListSearchType$ = createEffect(() => {
        return this.actions$.pipe(
            ofType(SidecarEfffectActions.setStoriesListSearchType),
            map(({ sidecarType, searchType }) => {
                if (!Object.values(SearchType).includes(searchType)) {
                    return FullscreenActions.noOpAction();
                }
                switch (sidecarType) {
                    case FullscreenLayoutAreas.SIDECAR_MAIN:
                        return MainSidecarActions.setStoriesListSearchType({ searchType });
                    case FullscreenLayoutAreas.SIDECAR_SECOND:
                        return SecondSidecarActions.setStoriesListSearchType({ searchType });
                    case FullscreenLayoutAreas.SIDECAR_THIRD:
                        return ThirdSidecarActions.setStoriesListSearchType({ searchType });
                    case FullscreenLayoutAreas.SIDECAR_FOURTH:
                        return FourthSidecarActions.setStoriesListSearchType({ searchType });
                }
                return FullscreenActions.noOpAction();
            })
        );
    });

    setTopicsListSearchType$ = createEffect(() => {
        return this.actions$.pipe(
            ofType(SidecarEfffectActions.setTopicsListSearchType),
            map(({ sidecarType, searchType }) => {
                if (!Object.values(SearchType).includes(searchType)) {
                    return FullscreenActions.noOpAction();
                }
                switch (sidecarType) {
                    case FullscreenLayoutAreas.SIDECAR_MAIN:
                        return MainSidecarActions.setTopicsListSearchType({ searchType });
                    case FullscreenLayoutAreas.SIDECAR_SECOND:
                        return SecondSidecarActions.setTopicsListSearchType({ searchType });
                    case FullscreenLayoutAreas.SIDECAR_THIRD:
                        return ThirdSidecarActions.setTopicsListSearchType({ searchType });
                    case FullscreenLayoutAreas.SIDECAR_FOURTH:
                        return FourthSidecarActions.setTopicsListSearchType({ searchType });
                }
                return FullscreenActions.noOpAction();
            })
        );
    });

    setRundownsListSearchType$ = createEffect(() => {
        return this.actions$.pipe(
            ofType(SidecarEfffectActions.setRundownsListSearchType),
            map(({ sidecarType, searchType }) => {
                if (!Object.values(SearchType).includes(searchType)) {
                    return FullscreenActions.noOpAction();
                }
                switch (sidecarType) {
                    case FullscreenLayoutAreas.SIDECAR_MAIN:
                        return MainSidecarActions.setRundownsListSearchType({ searchType });
                    case FullscreenLayoutAreas.SIDECAR_SECOND:
                        return SecondSidecarActions.setRundownsListSearchType({ searchType });
                    case FullscreenLayoutAreas.SIDECAR_THIRD:
                        return ThirdSidecarActions.setRundownsListSearchType({ searchType });
                    case FullscreenLayoutAreas.SIDECAR_FOURTH:
                        return FourthSidecarActions.setRundownsListSearchType({ searchType });
                }
                return FullscreenActions.noOpAction();
            })
        );
    });

    loadSidecarAssignmentsByVS(sidecarType: FullscreenLayoutAreas, viewState: SearchViewStateRecord) {
        console.log('loadSidecarAssignmentsByVS', sidecarType, viewState);
        if (viewState?.guid === SYSTEM_VS_GUID) {
            return of(SidecarEfffectActions.setCurrentTopicAssignments({ sidecarType }));
        }
        return from(this.playbookSearchService.getPlaybookResults(viewState)).pipe(
            map(data => data?.data),
            exhaustMap(results => {
                const actions = [];
                switch (sidecarType) {
                    case FullscreenLayoutAreas.SIDECAR_MAIN:
                        actions.push(MainSidecarActions.setAssignmentListGuids({ assignmentGuids: [...(results?.listGuids || [])] }));
                        break;

                    case FullscreenLayoutAreas.SIDECAR_SECOND:
                        actions.push(SecondSidecarActions.setAssignmentListGuids({ assignmentGuids: [...(results?.listGuids || [])] }));
                        break;

                    case FullscreenLayoutAreas.SIDECAR_THIRD:
                        actions.push(ThirdSidecarActions.setAssignmentListGuids({ assignmentGuids: [...(results?.listGuids || [])] }));
                        break;

                    case FullscreenLayoutAreas.SIDECAR_FOURTH:
                        actions.push(FourthSidecarActions.setAssignmentListGuids({ assignmentGuids: [...(results?.listGuids || [])] }));
                        break;

                    default:
                        break;
                }
                if (results?.topics) {
                    actions.push(TopicRecordActions.updateTopics({ records: [...(results.topics?.values() || [])] }));
                }
                if (results?.assignments) {
                    actions.push(AssignmentRecordActions.updateAssignments({ records: [...(results.assignments?.values() || [])] }));
                }
                return actions;
            }),
            catchError(() => of(FullscreenActions.noOpAction()))
        );
    }
    // Eof assignment sidecar effects

    // stories sidecar effects
    loadSidecarStoryViewStates$ = createEffect(() => {
        return this.actions$.pipe(
            ofType(SidecarEfffectActions.loadSidecarStoryViewStates),
            concatLatestFrom(() => [this.store.select(selectCurrentRecordType)]),
            switchMap(([{ sidecarType }, currentRecordType]) => {
                return from(this.viewStateService.getViewStateRecords('playbook', null, 'group')).pipe(
                    map(({ data }) => data?.views?.filter(vs => vs.viewStateType === ViewStateTypesEnum.SCRIPT)),
                    exhaustMap(viewsData => {
                        const actions = [];
                        const systemVs = new ViewStateRecord();
                        systemVs.guid = SYSTEM_VS_GUID;
                        systemVs.viewStateType = ViewStateTypesEnum.SCRIPT;
                        systemVs.title = currentRecordType === ObjectTypes.RUNDOWN ? SYSTEM_VS_NAME_RUNDOWN : SYSTEM_VS_NAME_TOPIC;

                        const views = [systemVs, ...viewsData];
                        switch (sidecarType) {
                            case FullscreenLayoutAreas.SIDECAR_MAIN:
                                actions.push(MainSidecarActions.setStoryViewStates({ viewStates: views }));
                                break;
                            case FullscreenLayoutAreas.SIDECAR_SECOND:
                                actions.push(SecondSidecarActions.setStoryViewStates({ viewStates: views }));
                                break;
                            case FullscreenLayoutAreas.SIDECAR_THIRD:
                                actions.push(ThirdSidecarActions.setStoryViewStates({ viewStates: views }));
                                break;
                            case FullscreenLayoutAreas.SIDECAR_FOURTH:
                                actions.push(FourthSidecarActions.setStoryViewStates({ viewStates: views }));
                                break;
                            default:
                                break;
                        }
                        const preselectedVS = views?.[0];
                        if (preselectedVS) {
                            actions.push(SidecarEfffectActions.setSidecarStoryCurrentViewState({ guid: preselectedVS.guid, sidecarType }));
                        }
                        if (actions.length > 0) {
                            return actions;
                        }
                        return of(FullscreenActions.noOpAction());
                    }),
                    catchError(() => of(FullscreenActions.noOpAction()))
                );
            })
        );
    });

    setSidecarStoryViewState$ = createEffect(() => {
        return this.actions$.pipe(
            ofType(SidecarEfffectActions.setSidecarStoryCurrentViewState),
            map(({ guid, sidecarType }) => {
                switch (sidecarType) {
                    case FullscreenLayoutAreas.SIDECAR_MAIN:
                        return MainSidecarActions.setCurrentStoryViewState({ guid });
                    case FullscreenLayoutAreas.SIDECAR_SECOND:
                        return SecondSidecarActions.setCurrentStoryViewState({ guid });
                    case FullscreenLayoutAreas.SIDECAR_THIRD:
                        return ThirdSidecarActions.setCurrentStoryViewState({ guid });
                    case FullscreenLayoutAreas.SIDECAR_FOURTH:
                        return FourthSidecarActions.setCurrentStoryViewState({ guid });
                    default:
                        break;
                }
                return FullscreenActions.noOpAction();
            })
        );
    });

    loadSidecarStories$ = createEffect(() => {
        return this.actions$.pipe(
            ofType(
                MainSidecarActions.setCurrentStoryViewState,
                SecondSidecarActions.setCurrentStoryViewState,
                ThirdSidecarActions.setCurrentStoryViewState,
                FourthSidecarActions.setCurrentStoryViewState,
                MainSidecarActions.reloadStoryBasedOnCurrentViewState,
                SecondSidecarActions.reloadStoryBasedOnCurrentViewState,
                ThirdSidecarActions.reloadStoryBasedOnCurrentViewState,
                FourthSidecarActions.reloadStoryBasedOnCurrentViewState
            ),
            concatLatestFrom(() => [
                this.store.select(selectSidecarMainCurrentStoryViewState),
                this.store.select(selectSidecarSecondCurrentStoryViewState),
                this.store.select(selectSidecarThirdCurrentStoryViewState),
                this.store.select(selectSidecarFourthCurrentStoryViewState),
                this.store.select(selectCurrentRecordType),
            ]),
            switchMap(([action, main, second, third, fourth, currentRecordType]) => {
                let viewState = null;
                let sidecarType = null;
                switch (action.type) {
                    case MainSidecarActions.setCurrentStoryViewState.type:
                    case MainSidecarActions.reloadStoryBasedOnCurrentViewState.type:
                        viewState = main;
                        sidecarType = FullscreenLayoutAreas.SIDECAR_MAIN;
                        break;

                    case SecondSidecarActions.setCurrentStoryViewState.type:
                    case SecondSidecarActions.reloadStoryBasedOnCurrentViewState.type:
                        viewState = second;
                        sidecarType = FullscreenLayoutAreas.SIDECAR_SECOND;
                        break;

                    case ThirdSidecarActions.setCurrentStoryViewState.type:
                    case ThirdSidecarActions.reloadStoryBasedOnCurrentViewState.type:
                        viewState = third;
                        sidecarType = FullscreenLayoutAreas.SIDECAR_THIRD;
                        break;

                    case FourthSidecarActions.setCurrentStoryViewState.type:
                    case FourthSidecarActions.reloadStoryBasedOnCurrentViewState.type:
                        viewState = fourth;
                        sidecarType = FullscreenLayoutAreas.SIDECAR_FOURTH;
                        break;

                    default:
                        break;
                }
                if (viewState?.guid) {
                    return this.loadSidecarStoriesByVS(sidecarType, viewState, currentRecordType);
                }
                return of(FullscreenActions.noOpAction());
            })
        );
    });

    reloadSidecarStorys$ = createEffect(() => {
        return this.actions$.pipe(
            ofType(SidecarEfffectActions.reloadSidecarStoryBasedOnCurrentViewState),
            map(action => {
                switch (action.sidecarType) {
                    case FullscreenLayoutAreas.SIDECAR_MAIN:
                        return MainSidecarActions.reloadStoryBasedOnCurrentViewState();
                    case FullscreenLayoutAreas.SIDECAR_SECOND:
                        return SecondSidecarActions.reloadStoryBasedOnCurrentViewState();
                    case FullscreenLayoutAreas.SIDECAR_THIRD:
                        return ThirdSidecarActions.reloadStoryBasedOnCurrentViewState();
                    case FullscreenLayoutAreas.SIDECAR_FOURTH:
                        return FourthSidecarActions.reloadStoryBasedOnCurrentViewState();
                    default:
                        return FullscreenActions.noOpAction();
                }
            })
        );
    });

    openCurrentTopic$ = createEffect(() => {
        return this.actions$.pipe(
            ofType(SidecarEfffectActions.openCurrentTopic),
            concatLatestFrom(() => [this.store.select(selectTopicRecordsMap)]),
            switchMap(([{ recordGuid, sidecarMenu }, topics]) => {
                const storeRecord = topics.get(recordGuid);
                if (!storeRecord) {
                    return of(FullscreenActions.noOpAction());
                }

                return from(this.topicService.getTopicContent(storeRecord.Database, storeRecord.Path.fullPath, storeRecord.Guid)).pipe(
                    filter(res => res.status),
                    map(res => res.data),
                    exhaustMap(rec => {
                        const actions = [];
                        actions.push(TopicRecordActions.updateTopics({ records: [rec] }));
                        if (rec.stories?.length > 0) {
                            actions.push(StoriesRecordActions.updateStories({ records: rec.stories }));
                        }

                        switch (sidecarMenu) {
                            case FullscreenLayoutAreas.SIDECAR_MAIN:
                                actions.push(MainSidecarActions.selectSidecarTopic({ guid: recordGuid }));
                                break;
                            case FullscreenLayoutAreas.SIDECAR_SECOND:
                                actions.push(SecondSidecarActions.selectSidecarTopic({ guid: recordGuid }));
                                break;
                            case FullscreenLayoutAreas.SIDECAR_THIRD:
                                actions.push(ThirdSidecarActions.selectSidecarTopic({ guid: recordGuid }));
                                break;
                            case FullscreenLayoutAreas.SIDECAR_FOURTH:
                                actions.push(FourthSidecarActions.selectSidecarTopic({ guid: recordGuid }));
                                break;
                        }

                        if (actions.length > 0) {
                            return actions;
                        }
                        return of(FullscreenActions.noOpAction());
                    })
                );
            })
        );
    });

    clearSelectedTopic$ = createEffect(() => {
        return this.actions$.pipe(
            ofType(SidecarEfffectActions.clearSelectedTopic),
            map(action => {
                switch (action.sidecarType) {
                    case FullscreenLayoutAreas.SIDECAR_MAIN:
                        return MainSidecarActions.selectSidecarTopic({ guid: null });
                    case FullscreenLayoutAreas.SIDECAR_SECOND:
                        return SecondSidecarActions.selectSidecarTopic({ guid: null });
                    case FullscreenLayoutAreas.SIDECAR_THIRD:
                        return ThirdSidecarActions.selectSidecarTopic({ guid: null });
                    case FullscreenLayoutAreas.SIDECAR_FOURTH:
                        return FourthSidecarActions.selectSidecarTopic({ guid: null });
                    default:
                        return FullscreenActions.noOpAction();
                }
            })
        );
    });

    onSidecarStorySearchTermChanged$ = createEffect(() => {
        return this.actions$.pipe(
            debounceTime(300),
            ofType(SidecarEfffectActions.searchStories),
            switchMap(({ sidecarType, searchTerm }) => {
                if (!searchTerm) {
                    return of(SidecarEfffectActions.clearSearchStory({ sidecarType }));
                }
                const vs: SearchViewStateRecord = new SearchViewStateRecord();
                vs.viewStateSearchQuery = searchTerm;
                vs.viewStateType = ViewStateTypesEnum.SCRIPT;
                return this.loadSidecarStoriesByVS(sidecarType, vs);
            })
        );
    });

    clearSidecarStorySearch$ = createEffect(() => {
        return this.actions$.pipe(
            ofType(SidecarEfffectActions.clearSearchStory),
            map(action => {
                switch (action.sidecarType) {
                    case FullscreenLayoutAreas.SIDECAR_MAIN:
                        return MainSidecarActions.setStoryListGuids({ storyGuids: [] });
                    case FullscreenLayoutAreas.SIDECAR_SECOND:
                        return SecondSidecarActions.setStoryListGuids({ storyGuids: [] });
                    case FullscreenLayoutAreas.SIDECAR_THIRD:
                        return ThirdSidecarActions.setStoryListGuids({ storyGuids: [] });
                    case FullscreenLayoutAreas.SIDECAR_FOURTH:
                        return FourthSidecarActions.setStoryListGuids({ storyGuids: [] });
                    default:
                        return FullscreenActions.noOpAction();
                }
            })
        );
    });

    clearSelectedStory$ = createEffect(() => {
        return this.actions$.pipe(
            ofType(SidecarEfffectActions.clearSelectedStory),
            map(action => {
                switch (action.sidecarType) {
                    case FullscreenLayoutAreas.SIDECAR_MAIN:
                        return MainSidecarActions.selectSidecarStory({ guid: null });
                    case FullscreenLayoutAreas.SIDECAR_SECOND:
                        return SecondSidecarActions.selectSidecarStory({ guid: null });
                    case FullscreenLayoutAreas.SIDECAR_THIRD:
                        return ThirdSidecarActions.selectSidecarStory({ guid: null });
                    case FullscreenLayoutAreas.SIDECAR_FOURTH:
                        return FourthSidecarActions.selectSidecarStory({ guid: null });
                    default:
                        return FullscreenActions.noOpAction();
                }
            })
        );
    });

    setCurrentTopicStories$ = createEffect(() => {
        return this.actions$.pipe(
            ofType(SidecarEfffectActions.setCurrentTopicStories),
            concatLatestFrom(() => [this.store.select(selectCurrentRecord), this.store.select(selectCurrentStoryParentTopic)]),
            map(([action, currentRecord, storyParentTopic]) => {
                const record = currentRecord?.Type === ObjectTypes.TOPIC ? currentRecord : storyParentTopic;
                switch (action.sidecarType) {
                    case FullscreenLayoutAreas.SIDECAR_MAIN:
                        return MainSidecarActions.setStoryListGuids({ storyGuids: [...((record as StorytellingTopicRecord)?.stories?.map(story => story?.Guid) || [])] });
                    case FullscreenLayoutAreas.SIDECAR_SECOND:
                        return SecondSidecarActions.setStoryListGuids({ storyGuids: [...((record as StorytellingTopicRecord)?.stories?.map(story => story?.Guid) || [])] });
                    case FullscreenLayoutAreas.SIDECAR_THIRD:
                        return ThirdSidecarActions.setStoryListGuids({ storyGuids: [...((record as StorytellingTopicRecord)?.stories?.map(story => story?.Guid) || [])] });
                    case FullscreenLayoutAreas.SIDECAR_FOURTH:
                        return FourthSidecarActions.setStoryListGuids({ storyGuids: [...((record as StorytellingTopicRecord)?.stories?.map(story => story?.Guid) || [])] });
                }
                return FullscreenActions.noOpAction();
            })
        );
    });

    setCurrentROStories$ = createEffect(() => {
        return this.actions$.pipe(
            ofType(SidecarEfffectActions.setCurrentROStories),
            concatLatestFrom(() => [this.store.select(selectCurrentRecord), this.store.select(selectCurrentStoryParentTopic)]),
            map(([action, currentRecord, storyParentTopic]) => {
                const record = currentRecord?.Type === ObjectTypes.RUNDOWN ? currentRecord : storyParentTopic;
                const storyGuids = [...((record as StorytellingRundownRecord)?.Sequence || [])].filter(guid => !!guid && guid !== '0');
                switch (action.sidecarType) {
                    case FullscreenLayoutAreas.SIDECAR_MAIN:
                        return MainSidecarActions.setStoryListGuids({ storyGuids });
                    case FullscreenLayoutAreas.SIDECAR_SECOND:
                        return SecondSidecarActions.setStoryListGuids({ storyGuids });
                    case FullscreenLayoutAreas.SIDECAR_THIRD:
                        return ThirdSidecarActions.setStoryListGuids({ storyGuids });
                    case FullscreenLayoutAreas.SIDECAR_FOURTH:
                        return FourthSidecarActions.setStoryListGuids({ storyGuids });
                }
                return FullscreenActions.noOpAction();
            })
        );
    });

    loadSidecarStoriesByVS(sidecarType: FullscreenLayoutAreas, viewState: SearchViewStateRecord, currentRecordType?: ObjectTypes) {
        if (viewState?.guid === SYSTEM_VS_GUID) {
            switch (currentRecordType) {
                case ObjectTypes.TOPIC:
                case ObjectTypes.SCRIPT:
                    return of(SidecarEfffectActions.setCurrentTopicStories({ sidecarType }));
                case ObjectTypes.RUNDOWN:
                    return of(SidecarEfffectActions.setCurrentROStories({ sidecarType }));
                default:
                    return of(FullscreenActions.noOpAction());
            }
        }
        return from(this.playbookSearchService.getPlaybookResults(viewState)).pipe(
            map(data => data?.data),
            exhaustMap(results => {
                const actions = [];
                switch (sidecarType) {
                    case FullscreenLayoutAreas.SIDECAR_MAIN:
                        actions.push(MainSidecarActions.setStoryListGuids({ storyGuids: [...(results?.listGuids || [])] }));
                        break;

                    case FullscreenLayoutAreas.SIDECAR_SECOND:
                        actions.push(SecondSidecarActions.setStoryListGuids({ storyGuids: [...(results?.listGuids || [])] }));
                        break;

                    case FullscreenLayoutAreas.SIDECAR_THIRD:
                        actions.push(ThirdSidecarActions.setStoryListGuids({ storyGuids: [...(results?.listGuids || [])] }));
                        break;

                    case FullscreenLayoutAreas.SIDECAR_FOURTH:
                        actions.push(FourthSidecarActions.setStoryListGuids({ storyGuids: [...(results?.listGuids || [])] }));
                        break;

                    default:
                        break;
                }
                if (results?.topics) {
                    actions.push(TopicRecordActions.updateTopics({ records: [...(results.topics?.values() || [])] }));
                }
                if (results?.stories) {
                    actions.push(StoriesRecordActions.updateStories({ records: [...(results.stories?.values() || [])] }));
                }
                return actions;
            }),
            catchError(() => of(FullscreenActions.noOpAction()))
        );
    }

    openStoryInSidecar$ = createEffect(() => {
        return this.actions$.pipe(
            ofType(SidecarEfffectActions.openStoryInSidecar),
            concatLatestFrom(() => [this.store.select(selectAllStoriesMap)]),
            switchMap(([{ guid, sidecarType }, stories]) => {
                const storeRecord = stories.get(guid);
                if (!storeRecord) {
                    return of(FullscreenActions.noOpAction());
                }

                let sidecarStoryAction = null;
                switch (sidecarType) {
                    case FullscreenLayoutAreas.SIDECAR_MAIN:
                        sidecarStoryAction = MainSidecarActions.selectSidecarStory({ guid: storeRecord.Guid });
                        break;
                    case FullscreenLayoutAreas.SIDECAR_SECOND:
                        sidecarStoryAction = SecondSidecarActions.selectSidecarStory({ guid: storeRecord.Guid });
                        break;
                    case FullscreenLayoutAreas.SIDECAR_THIRD:
                        sidecarStoryAction = ThirdSidecarActions.selectSidecarStory({ guid: storeRecord.Guid });
                        break;
                    case FullscreenLayoutAreas.SIDECAR_FOURTH:
                        sidecarStoryAction = FourthSidecarActions.selectSidecarStory({ guid: storeRecord.Guid });
                        break;
                }

                if (storeRecord.isNewRecord) {
                    return sidecarStoryAction ? of(sidecarStoryAction) : of(FullscreenActions.noOpAction());
                }

                return from(
                    this.basicContentService.getRecord<StorytellingStoryRecord>(storeRecord.Database || this.nomWebServicesJson.getDatabase(), storeRecord.Path.fullPath, storeRecord.Guid)
                ).pipe(
                    filter(res => res.status),
                    map(res => res.data),
                    map(rec => [StoriesRecordActions.updateStories({ records: [rec] }), ...(sidecarStoryAction ? [sidecarStoryAction] : [])]),
                    switchMap(actions => actions)
                );
            })
        );
    });
    // Eof stories sidecar effects

    onNavigateStory$ = createEffect(() => {
        return this.actions$.pipe(
            ofType(SidecarEfffectActions.navigateStory),
            concatLatestFrom(action => {
                return [
                    this.store.select(selectSidecarStories(action.sidecarMenu)),
                    this.store.select(selectSidecarSelectedStoryIndex(action.sidecarMenu)),
                    this.store.select(selectSidecarCurrentStory(action.sidecarMenu)),
                ];
            }),
            exhaustMap(([action, storyList, selectedIdx, currentStory]) => {
                const actions = [];
                if (selectedIdx == null || selectedIdx < 0 || storyList?.length === 0 || selectedIdx >= storyList.length) {
                    return of(FullscreenActions.noOpAction());
                }
                const story = { ...storyList[selectedIdx] };
                let nextStoryGuid = null;
                switch (action.direction) {
                    case NavigationDirection.PREVIOUS:
                        if (selectedIdx === 0) {
                            nextStoryGuid = storyList[storyList.length - 1]?.Guid;
                        } else {
                            nextStoryGuid = storyList[selectedIdx - 1]?.Guid;
                        }
                        break;
                    case NavigationDirection.NEXT:
                        if (selectedIdx === storyList.length - 1) {
                            nextStoryGuid = storyList[0]?.Guid;
                        } else {
                            nextStoryGuid = storyList[selectedIdx + 1]?.Guid;
                        }
                        break;
                    case NavigationDirection.FIRST:
                        nextStoryGuid = storyList[0]?.Guid;
                        break;
                    case NavigationDirection.LAST:
                        nextStoryGuid = storyList[storyList.length - 1]?.Guid;
                        break;
                }

                if (action.saveCurrentStory) {
                    actions.push(
                        ...[
                            SidecarEfffectActions.openStoryInSidecar({ guid: nextStoryGuid, sidecarType: action.sidecarMenu }),
                            RecordHttpActions.saveRecordChanges({
                                record: story,
                                fieldsToUpdate: [
                                    { key: PSTFieldNames.Type, value: ObjectTypes.SCRIPT },
                                    { key: PSTFieldNames.PSTRichText, value: story?.text },
                                    ...(story?.TextCommands ? [{ key: PSTFieldNames.TextCommands, value: story?.TextCommands }] : []),
                                ],
                            }),
                        ]
                    );
                    return from(actions);
                }

                actions.push(StoriesRecordActions.setStoryHasUnsavedChanges({ storyGuid: currentStory?.Guid, hasUnsavedChanges: false }));
                actions.push(SidecarEfffectActions.openStoryInSidecar({ guid: nextStoryGuid, sidecarType: action.sidecarMenu }));

                return actions;
            })
        );
    });

    setSelectedStoryHasUnsavedChanges$ = createEffect(() => {
        return this.actions$.pipe(
            ofType(SidecarEfffectActions.setSelectedStoryHasUnsavedChanges),
            map(action => {
                switch (action.sidecarMenu) {
                    case FullscreenLayoutAreas.SIDECAR_MAIN:
                        return MainSidecarActions.setSelectedStoryHasUnsavedChanges({ hasUnsavedChanges: action.hasUnsavedChanges });
                    case FullscreenLayoutAreas.SIDECAR_SECOND:
                        return SecondSidecarActions.setSelectedStoryHasUnsavedChanges({ hasUnsavedChanges: action.hasUnsavedChanges });
                    case FullscreenLayoutAreas.SIDECAR_THIRD:
                        return ThirdSidecarActions.setSelectedStoryHasUnsavedChanges({ hasUnsavedChanges: action.hasUnsavedChanges });
                    case FullscreenLayoutAreas.SIDECAR_FOURTH:
                        return FourthSidecarActions.setSelectedStoryHasUnsavedChanges({ hasUnsavedChanges: action.hasUnsavedChanges });
                    default:
                        return FullscreenActions.noOpAction();
                }
            })
        );
    });

    setCurrentROSelectedStory$ = createEffect(() => {
        return this.actions$.pipe(
            ofType(SidecarEfffectActions.setCurrentROSelectedStory),
            map(action => {
                switch (action.sidecarMenu) {
                    case FullscreenLayoutAreas.SIDECAR_MAIN:
                        return MainSidecarActions.setCurrentROSelectedStory({ guid: action.guid });
                    case FullscreenLayoutAreas.SIDECAR_SECOND:
                        return SecondSidecarActions.setCurrentROSelectedStory({ guid: action.guid });
                    case FullscreenLayoutAreas.SIDECAR_THIRD:
                        return ThirdSidecarActions.setCurrentROSelectedStory({ guid: action.guid });
                    case FullscreenLayoutAreas.SIDECAR_FOURTH:
                        return FourthSidecarActions.setCurrentROSelectedStory({ guid: action.guid });
                }
                return FullscreenActions.noOpAction();
            })
        );
    });

    setCurrentTopicSelectedStory$ = createEffect(() => {
        return this.actions$.pipe(
            ofType(SidecarEfffectActions.setCurrentTopicSelectedStory),
            map(action => {
                switch (action.sidecarMenu) {
                    case FullscreenLayoutAreas.SIDECAR_MAIN:
                        return MainSidecarActions.setCurrentTopicSelectedStory({ guid: action.guid });
                    case FullscreenLayoutAreas.SIDECAR_SECOND:
                        return SecondSidecarActions.setCurrentTopicSelectedStory({ guid: action.guid });
                    case FullscreenLayoutAreas.SIDECAR_THIRD:
                        return ThirdSidecarActions.setCurrentTopicSelectedStory({ guid: action.guid });
                    case FullscreenLayoutAreas.SIDECAR_FOURTH:
                        return FourthSidecarActions.setCurrentTopicSelectedStory({ guid: action.guid });
                }
                return FullscreenActions.noOpAction();
            })
        );
    });

    setCurrentTopicSelectedAssignment$ = createEffect(() => {
        return this.actions$.pipe(
            ofType(SidecarEfffectActions.setCurrentTopicSelectedAssignment),
            map(action => {
                switch (action.sidecarMenu) {
                    case FullscreenLayoutAreas.SIDECAR_MAIN:
                        return MainSidecarActions.setCurrentTopicSelectedAssignment({ guid: action.guid });
                    case FullscreenLayoutAreas.SIDECAR_SECOND:
                        return SecondSidecarActions.setCurrentTopicSelectedAssignment({ guid: action.guid });
                    case FullscreenLayoutAreas.SIDECAR_THIRD:
                        return ThirdSidecarActions.setCurrentTopicSelectedAssignment({ guid: action.guid });
                    case FullscreenLayoutAreas.SIDECAR_FOURTH:
                        return FourthSidecarActions.setCurrentTopicSelectedAssignment({ guid: action.guid });
                }
                return FullscreenActions.noOpAction();
            })
        );
    });

    onNavigateStoryInTopic$ = createEffect(() => {
        return this.actions$.pipe(
            ofType(SidecarEfffectActions.navigateStoryInCurrentTopic),
            concatLatestFrom(action => {
                return [this.store.select(selectSidecarCurrentTopicStoryGuids(action.sidecarMenu)), this.store.select(selectSidecarCurrentTopicSelectedStory(action.sidecarMenu))];
            }),
            map(([action, currentTopicStories, lastSelectedStory]) => {
                const storyList = currentTopicStories;
                const selectedIdx = storyList?.indexOf(lastSelectedStory?.Guid);
                if (selectedIdx == null || selectedIdx < 0 || !storyList || storyList?.length === 0) {
                    return FullscreenActions.noOpAction();
                }
                let storyGuid = lastSelectedStory.Guid;
                switch (action.direction) {
                    case NavigationDirection.PREVIOUS:
                        if (selectedIdx === 0) {
                            storyGuid = storyList[storyList.length - 1];
                        } else {
                            storyGuid = storyList[selectedIdx - 1];
                        }
                        break;
                    case NavigationDirection.NEXT:
                        if (selectedIdx === storyList.length - 1) {
                            storyGuid = storyList[0];
                        } else {
                            storyGuid = storyList[selectedIdx + 1];
                        }
                        break;
                    case NavigationDirection.FIRST:
                        storyGuid = storyList[0];
                        break;
                    case NavigationDirection.LAST:
                        storyGuid = storyList[storyList.length - 1];
                        break;
                }

                return SidecarEfffectActions.setCurrentTopicSelectedStory({
                    guid: storyGuid,
                    sidecarMenu: action.sidecarMenu,
                });
            })
        );
    });

    navigateAssignmentInCurrentTopic$ = createEffect(() => {
        return this.actions$.pipe(
            ofType(SidecarEfffectActions.navigateAssignmentInCurrentTopic),
            concatLatestFrom(action => {
                return [this.store.select(selectSidecarSelectedTopic(action.sidecarMenu)), this.store.select(selectSidecarCurrentTopicSelectedAssignment(action.sidecarMenu))];
            }),
            map(([action, currentTopic, lastSelectedAssignment]) => {
                const assignmentList = currentTopic?.Sequence;
                const selectedIdx = assignmentList?.indexOf(lastSelectedAssignment?.Guid);
                if (selectedIdx == null || selectedIdx < 0 || !assignmentList || assignmentList?.length === 0) {
                    return FullscreenActions.noOpAction();
                }
                let assignmentGuid = lastSelectedAssignment.Guid;
                switch (action.direction) {
                    case NavigationDirection.PREVIOUS:
                        if (selectedIdx === 0) {
                            assignmentGuid = assignmentList[assignmentList.length - 1];
                        } else {
                            assignmentGuid = assignmentList[selectedIdx - 1];
                        }
                        break;
                    case NavigationDirection.NEXT:
                        if (selectedIdx === assignmentList.length - 1) {
                            assignmentGuid = assignmentList[0];
                        } else {
                            assignmentGuid = assignmentList[selectedIdx + 1];
                        }
                        break;
                    case NavigationDirection.FIRST:
                        assignmentGuid = assignmentList[0];
                        break;
                    case NavigationDirection.LAST:
                        assignmentGuid = assignmentList[assignmentList.length - 1];
                        break;
                }

                return SidecarEfffectActions.setCurrentTopicSelectedAssignment({
                    guid: assignmentGuid,
                    sidecarMenu: action.sidecarMenu,
                });
            })
        );
    });

    onNavigateStoryInRO$ = createEffect(() => {
        return this.actions$.pipe(
            ofType(SidecarEfffectActions.navigateStoryInCurrentRO),
            concatLatestFrom(action => {
                return [this.store.select(selectSidecarSelectedRundown(action.sidecarMenu)), this.store.select(selectSidecarCurrentRundownSelectedStory(action.sidecarMenu))];
            }),
            map(([action, currentRO, lastSelectedStory]) => {
                const storyList = currentRO?.Sequence;
                const selectedIdx = storyList?.indexOf(lastSelectedStory?.Guid);
                if (selectedIdx == null || selectedIdx < 0 || !storyList || storyList?.length === 0) {
                    return FullscreenActions.noOpAction();
                }
                let storyGuid = lastSelectedStory.Guid;
                switch (action.direction) {
                    case NavigationDirection.PREVIOUS:
                        if (selectedIdx === 0) {
                            storyGuid = storyList[storyList.length - 1];
                        } else {
                            storyGuid = storyList[selectedIdx - 1];
                        }
                        break;
                    case NavigationDirection.NEXT:
                        if (selectedIdx === storyList.length - 1) {
                            storyGuid = storyList[0];
                        } else {
                            storyGuid = storyList[selectedIdx + 1];
                        }
                        break;
                    case NavigationDirection.FIRST:
                        storyGuid = storyList[0];
                        break;
                    case NavigationDirection.LAST:
                        storyGuid = storyList[storyList.length - 1];
                        break;
                }

                return SidecarEfffectActions.setCurrentROSelectedStory({
                    guid: storyGuid,
                    sidecarMenu: action.sidecarMenu,
                });
            })
        );
    });

    reloadSidecarItemsForTheCurrentRecord$ = createEffect(() => {
        return this.actions$.pipe(
            ofType(
                OpenFullscreenModalActions.openRundownFullscreenDialog,
                OpenFullscreenModalActions.openStoryFullscreenDialog,
                OpenFullscreenModalActions.openTopicFullscreenDialog,
                FullscreenActions.openTopic,
                FullscreenActions.openStory,
                FullscreenActions.openRundown
            ),
            concatLatestFrom(() => {
                return [
                    this.store.select(selectSidecarMainCurrentStoryViewState),
                    this.store.select(selectSidecarSecondCurrentStoryViewState),
                    this.store.select(selectSidecarThirdCurrentStoryViewState),
                    this.store.select(selectSidecarFourthCurrentStoryViewState),
                    this.store.select(selectSidecarMainCurrentAssignmentViewState),
                    this.store.select(selectSidecarSecondCurrentAssignmentViewState),
                    this.store.select(selectSidecarThirdCurrentAssignmentViewState),
                    this.store.select(selectSidecarFourthCurrentAssignmentViewState),
                ];
            }),
            exhaustMap(([_, mainStoryVS, secondStoryVS, thirdStoryVS, fourthStoryVS, mainAssignmentVS, secondAssignmentVS, thirdAssignmentVS, fourthAssignmentVS]) => {
                const actions = [];
                if (mainStoryVS?.guid === SYSTEM_VS_GUID) {
                    actions.push(SidecarEfffectActions.reloadSidecarStoryBasedOnCurrentViewState({ sidecarType: FullscreenLayoutAreas.SIDECAR_MAIN }));
                }
                if (secondStoryVS?.guid === SYSTEM_VS_GUID) {
                    actions.push(SidecarEfffectActions.reloadSidecarStoryBasedOnCurrentViewState({ sidecarType: FullscreenLayoutAreas.SIDECAR_SECOND }));
                }
                if (thirdStoryVS?.guid === SYSTEM_VS_GUID) {
                    actions.push(SidecarEfffectActions.reloadSidecarStoryBasedOnCurrentViewState({ sidecarType: FullscreenLayoutAreas.SIDECAR_THIRD }));
                }
                if (fourthStoryVS?.guid === SYSTEM_VS_GUID) {
                    actions.push(SidecarEfffectActions.reloadSidecarStoryBasedOnCurrentViewState({ sidecarType: FullscreenLayoutAreas.SIDECAR_FOURTH }));
                }
                if (mainAssignmentVS?.guid === SYSTEM_VS_GUID) {
                    actions.push(SidecarEfffectActions.reloadSidecarAssignmentBasedOnCurrentViewState({ sidecarType: FullscreenLayoutAreas.SIDECAR_MAIN }));
                }
                if (secondAssignmentVS?.guid === SYSTEM_VS_GUID) {
                    actions.push(SidecarEfffectActions.reloadSidecarAssignmentBasedOnCurrentViewState({ sidecarType: FullscreenLayoutAreas.SIDECAR_SECOND }));
                }
                if (thirdAssignmentVS?.guid === SYSTEM_VS_GUID) {
                    actions.push(SidecarEfffectActions.reloadSidecarAssignmentBasedOnCurrentViewState({ sidecarType: FullscreenLayoutAreas.SIDECAR_THIRD }));
                }
                if (fourthAssignmentVS?.guid === SYSTEM_VS_GUID) {
                    actions.push(SidecarEfffectActions.reloadSidecarAssignmentBasedOnCurrentViewState({ sidecarType: FullscreenLayoutAreas.SIDECAR_FOURTH }));
                }
                if (actions.length > 0) {
                    return actions;
                }
                return of(FullscreenActions.noOpAction());
            })
        );
    });

    setCurrentMosItem$ = createEffect(() => {
        return this.actions$.pipe(
            ofType(SidecarEfffectActions.setCurrentMosItem),
            map(action => {
                switch (action.sidecarMenu) {
                    case FullscreenLayoutAreas.SIDECAR_MAIN:
                        return MainSidecarActions.setCurrentMosItem({ mosId: action.mosId });
                    case FullscreenLayoutAreas.SIDECAR_SECOND:
                        return SecondSidecarActions.setCurrentMosItem({ mosId: action.mosId });
                    case FullscreenLayoutAreas.SIDECAR_THIRD:
                        return ThirdSidecarActions.setCurrentMosItem({ mosId: action.mosId });
                    case FullscreenLayoutAreas.SIDECAR_FOURTH:
                        return FourthSidecarActions.setCurrentMosItem({ mosId: action.mosId });
                }
                return FullscreenActions.noOpAction();
            })
        );
    });

    openMosPluginInSidecar$ = createEffect(() => {
        return this.actions$.pipe(
            ofType(SidecarEfffectActions.openMosPluginInSidecar),
            exhaustMap(action => {
                const actions = [];
                const currentFullscreenLayout = this.store.selectSignal(selectLayoutTemplate)();

                if (currentFullscreenLayout === FullscreenLayoutTemplate.ONLY_MAIN) {
                    actions.push(FullscreenActions.setFullscreenTemplate({ template: FullscreenLayoutTemplate.BASIC }));
                }

                switch (action.sidecarMenu) {
                    case FullscreenLayoutAreas.SIDECAR_MAIN:
                        actions.push(MainSidecarActions.setCurrentMosItem({ mosId: action.mosId }));
                        actions.push(MainSidecarActions.selectSidecarMenu({ menu: SidecarMenu.MOS }));
                        break;
                    case FullscreenLayoutAreas.SIDECAR_SECOND:
                        actions.push(SecondSidecarActions.setCurrentMosItem({ mosId: action.mosId }));
                        actions.push(SecondSidecarActions.selectSidecarMenu({ menu: SidecarMenu.MOS }));
                        break;
                    case FullscreenLayoutAreas.SIDECAR_THIRD:
                        actions.push(ThirdSidecarActions.setCurrentMosItem({ mosId: action.mosId }));
                        actions.push(ThirdSidecarActions.selectSidecarMenu({ menu: SidecarMenu.MOS }));
                        break;
                    case FullscreenLayoutAreas.SIDECAR_FOURTH:
                        actions.push(FourthSidecarActions.setCurrentMosItem({ mosId: action.mosId }));
                        actions.push(FourthSidecarActions.selectSidecarMenu({ menu: SidecarMenu.MOS }));
                        break;
                }

                return actions;
            })
        );
    });
}
